import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../../redux/slices/userSlice';
import { Container, Row, Col, Accordion, Spinner, Button, Dropdown, Badge, Alert, Nav, Modal, Form, Table } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
    Zap, FileText, BookOpen, ArrowLeft, FolderOpen, BarChart2, Presentation, Sparkles,
    Calendar, FlaskConical, Trophy, Upload, Share2, Download, Send, Clock, ChevronRight,
    MonitorPlay, BookOpenCheck, LayoutDashboard, RefreshCw, Link, Copy, Check,
} from 'lucide-react';
import api, {
    getEvidenceAskOSEngagement,
    getEvidenceConceptDifficulty,
    generateLessonPlan,
    generateCourseLessonPlan,
    generateLabSession,
    generateQuiz,
    publishQuiz,
    pushQuizLinkToClassroom,
    listCopilotClassroomCourses,
    pushMaterialToMoodle,
    pushMaterialToClassroom,
    uploadCopilotMaterial,
    generateContentFromText,
    getCopilotProfile,
    updateCopilotProfile,
} from '../../api/api';
import TeachingCopilotChat from './TeachingCopilotChat';
import TeachingMaterialEditor from './TeachingMaterialEditor';
import { showToast } from '../../utils/toast';

// ─── SVG Bar Chart ────────────────────────────────────────────────────────────
function MiniBarChart({ data = [], labelKey, valueKey, color = '#6366f1', colorFn, height = 110 }) {
    if (!data.length) return <div style={{ color: '#94a3b8', fontSize: 11, padding: 8 }}>No data</div>;
    const max = Math.max(...data.map(d => Number(d[valueKey]) || 0), 1);
    const barW = Math.max(18, Math.min(44, Math.floor(480 / data.length) - 6));
    const gap = 6;
    const svgW = data.length * (barW + gap);
    return (
        <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
            <svg width={svgW} height={height + 34} style={{ display: 'block' }}>
                {data.map((d, i) => {
                    const v = Number(d[valueKey]) || 0;
                    const h = Math.max(3, (v / max) * height);
                    const x = i * (barW + gap);
                    const fill = colorFn ? colorFn(d) : color;
                    const label = String(d[labelKey] || '');
                    return (
                        <g key={i}>
                            <rect x={x} y={height - h} width={barW} height={h} fill={fill} rx={4} opacity={0.88} />
                            <text x={x + barW / 2} y={height - h - 4} textAnchor="middle" fontSize={9} fill={fill} fontWeight="700">{v || ''}</text>
                            <text x={x + barW / 2} y={height + 12} textAnchor="middle" fontSize={8} fill="#64748b"
                                style={{ cursor: 'default' }}>
                                {label.length > 10 ? label.slice(0, 9) + '…' : label}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

// ─── Student Analytics Panel ──────────────────────────────────────────────────
function StudentAnalyticsPanel({ tenantSlug, project, activeTab, setActiveTab, onBack }) {
    const [engagement, setEngagement] = useState(null);
    const [difficulty, setDifficulty] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [engFilter, setEngFilter] = useState('');
    const [engSort, setEngSort] = useState({ col: null, asc: true });
    const [diffFilter, setDiffFilter] = useState('');
    const [diffSort, setDiffSort] = useState({ col: null, asc: true });

    useEffect(() => {
        setLoading(true); setError(null);
        Promise.all([
            getEvidenceAskOSEngagement(tenantSlug, project.id),
            getEvidenceConceptDifficulty(tenantSlug, project.id),
        ])
            .then(([engRes, diffRes]) => { setEngagement(engRes.data); setDifficulty(diffRes.data); })
            .catch(() => setError('Failed to load analytics. Ensure students have started AskOS sessions.'))
            .finally(() => setLoading(false));
    }, [tenantSlug, project.id]);

    const priorityColour = { high: '#dc3545', medium: '#f59e0b', low: '#6c757d' };

    const sortRows = (rows, { col, asc }) => {
        if (!col) return rows;
        return [...rows].sort((a, b) => {
            const av = a[col], bv = b[col];
            if (av == null) return 1; if (bv == null) return -1;
            return (av < bv ? -1 : av > bv ? 1 : 0) * (asc ? 1 : -1);
        });
    };
    const SortTh = ({ col, label, state, setState }) => (
        <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
            onClick={() => setState(s => ({ col, asc: s.col === col ? !s.asc : true }))}>
            {label} {state.col === col ? (state.asc ? '▲' : '▼') : <span style={{ opacity: .3 }}>⇅</span>}
        </th>
    );

    const engRows = sortRows(
        (engagement?.rows || []).filter(r =>
            !engFilter || [r.student_email, r.concept_name, r.language].some(v => String(v || '').toLowerCase().includes(engFilter.toLowerCase()))
        ),
        engSort
    );
    const diffRows = sortRows(
        (difficulty?.rows || []).filter(r =>
            !diffFilter || String(r.concept_name || '').toLowerCase().includes(diffFilter.toLowerCase())
        ),
        diffSort
    );

    const engChartData = (engagement?.concept_aggregates || []).slice(0, 12).map(c => ({ name: c.concept_name, sessions: c.sessions, questions: c.questions }));
    const diffChartData = diffRows.slice(0, 12).map(r => ({ name: r.concept_name, score: r.difficulty_score || 0, priority: r.support_priority }));

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #e9ecef', background: '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <button className="btn btn-sm btn-link text-dark p-0 d-flex align-items-center gap-1" onClick={onBack}>
                    <ArrowLeft size={15} /> Back
                </button>
                <BarChart2 size={16} color="#059669" />
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Student Analytics</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{project.name}</div>
                </div>
            </div>
            {/* Tab bar */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e9ecef', flexShrink: 0 }}>
                <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                    <Nav.Item><Nav.Link eventKey="engagement" style={{ fontSize: 12 }}>AskOS Engagement</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="difficulty" style={{ fontSize: 12 }}>Concept Difficulty</Nav.Link></Nav.Item>
                </Nav>
            </div>
            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8faff' }}>
                {loading && <div className="text-center py-5"><Spinner animation="border" size="sm" aria-hidden="true" /></div>}
                {error && <Alert variant="warning">{error}</Alert>}

                {/* ── Engagement tab ── */}
                {!loading && !error && activeTab === 'engagement' && engagement && (<>
                    {/* Stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
                        {[
                            { label: 'Total Sessions', value: engagement.summary?.total_sessions ?? 0, color: '#6366f1', bg: '#f0f0ff' },
                            { label: 'Unique Students', value: engagement.summary?.unique_students ?? 0, color: '#0891b2', bg: '#f0fbff' },
                            { label: 'Questions Asked', value: engagement.summary?.total_questions ?? 0, color: '#059669', bg: '#f0fdf4' },
                            { label: 'Voice Sessions', value: engagement.summary?.voice_sessions ?? 0, color: '#d97706', bg: '#fffbeb' },
                        ].map(s => (
                            <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
                                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                    {/* Charts row */}
                    {engChartData.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                            <div style={{ background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 1px 6px rgba(0,0,0,.06)' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Sessions per Concept</div>
                                <MiniBarChart data={engChartData} labelKey="name" valueKey="sessions" color="#6366f1" />
                            </div>
                            <div style={{ background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 1px 6px rgba(0,0,0,.06)' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Questions per Concept</div>
                                <MiniBarChart data={engChartData} labelKey="name" valueKey="questions" color="#0891b2" />
                            </div>
                        </div>
                    )}
                    {/* Filterable table */}
                    <div style={{ background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 1px 6px rgba(0,0,0,.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', flex: 1 }}>Session Details</div>
                            <input type="text" placeholder="Filter by student, concept, language…" value={engFilter}
                                onChange={e => setEngFilter(e.target.value)}
                                style={{ fontSize: 11, border: '1px solid #e9ecef', borderRadius: 8, padding: '4px 10px', width: 240, outline: 'none' }} />
                            {engFilter && <button style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }} onClick={() => setEngFilter('')}>✕</button>}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-sm table-hover mb-0" style={{ fontSize: 11 }}>
                                <thead style={{ background: '#f8faff' }}>
                                    <tr>
                                        <SortTh col="student_email" label="Student" state={engSort} setState={setEngSort} />
                                        <SortTh col="concept_name" label="Concept" state={engSort} setState={setEngSort} />
                                        <SortTh col="questions_asked" label="Questions" state={engSort} setState={setEngSort} />
                                        <SortTh col="language" label="Language" state={engSort} setState={setEngSort} />
                                        <SortTh col="voice_used" label="Voice" state={engSort} setState={setEngSort} />
                                        <SortTh col="last_interaction" label="Last Active" state={engSort} setState={setEngSort} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {engRows.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">No sessions match the filter.</td></tr>}
                                    {engRows.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.student_email}</td>
                                            <td>{r.concept_name}</td>
                                            <td className="text-center">{r.questions_asked}</td>
                                            <td>{r.language || '—'}</td>
                                            <td className="text-center">{r.voice_used ? <span style={{ color: '#059669' }}>✔</span> : '—'}</td>
                                            <td style={{ color: '#94a3b8' }}>{(r.last_interaction || '—').slice(0, 10)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {engRows.length > 0 && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>{engRows.length} record{engRows.length !== 1 ? 's' : ''}</div>}
                    </div>
                </>)}

                {/* ── Difficulty tab ── */}
                {!loading && !error && activeTab === 'difficulty' && difficulty && (<>
                    {/* Stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                        {[
                            { label: 'Total Concepts', value: difficulty.summary?.total_concepts ?? 0, color: '#6366f1', bg: '#f0f0ff' },
                            { label: 'High Priority', value: difficulty.summary?.high_priority ?? 0, color: '#dc2626', bg: '#fff1f1' },
                            { label: 'Unvisited Concepts', value: difficulty.summary?.unvisited_concepts ?? 0, color: '#94a3b8', bg: '#f8faff' },
                        ].map(s => (
                            <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
                                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                    {/* Chart */}
                    {diffChartData.length > 0 && (
                        <div style={{ background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 1px 6px rgba(0,0,0,.06)', marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Difficulty Score by Concept</div>
                            <MiniBarChart
                                data={diffChartData} labelKey="name" valueKey="score"
                                colorFn={d => d.priority === 'high' ? '#dc2626' : d.priority === 'medium' ? '#f59e0b' : '#94a3b8'}
                            />
                            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                                {[['High', '#dc2626'], ['Medium', '#f59e0b'], ['Low', '#94a3b8']].map(([l, c]) => (
                                    <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#64748b' }}>
                                        <div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />{l} priority
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Filterable table */}
                    <div style={{ background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 1px 6px rgba(0,0,0,.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', flex: 1 }}>Concept Difficulty Details</div>
                            <input type="text" placeholder="Filter by concept…" value={diffFilter}
                                onChange={e => setDiffFilter(e.target.value)}
                                style={{ fontSize: 11, border: '1px solid #e9ecef', borderRadius: 8, padding: '4px 10px', width: 200, outline: 'none' }} />
                            {diffFilter && <button style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 12 }} onClick={() => setDiffFilter('')}>✕</button>}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-sm table-hover mb-0" style={{ fontSize: 11 }}>
                                <thead style={{ background: '#f8faff' }}>
                                    <tr>
                                        <SortTh col="concept_name" label="Concept" state={diffSort} setState={setDiffSort} />
                                        <SortTh col="difficulty_score" label="Difficulty Score" state={diffSort} setState={setDiffSort} />
                                        <SortTh col="student_sessions" label="Sessions" state={diffSort} setState={setDiffSort} />
                                        <SortTh col="repeated_doubts" label="Repeated Doubts" state={diffSort} setState={setDiffSort} />
                                        <SortTh col="support_priority" label="Priority" state={diffSort} setState={setDiffSort} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {diffRows.map((r, i) => (
                                        <tr key={i} style={{ background: r.support_priority === 'high' ? '#fff5f5' : r.support_priority === 'medium' ? '#fffde7' : '' }}>
                                            <td>{r.concept_name}</td>
                                            <td className="text-center">{r.difficulty_score ?? '—'}</td>
                                            <td className="text-center">{r.student_sessions}</td>
                                            <td className="text-center">{r.repeated_doubts}</td>
                                            <td><span style={{ background: priorityColour[r.support_priority], color: '#fff', fontSize: 9, borderRadius: 20, padding: '2px 8px', fontWeight: 600, display: 'inline-block' }}>{r.support_priority}</span></td>
                                        </tr>
                                    ))}
                                    {diffRows.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">No concepts match the filter.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        {diffRows.length > 0 && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>{diffRows.length} concept{diffRows.length !== 1 ? 's' : ''}</div>}
                    </div>
                </>)}
            </div>
        </div>
    );
}

// ─── Q.Bank Review Card ───────────────────────────────────────────────────────
function QBankReviewCard({ review, busy, tenantSlug, onApprove, onReject }) {
    const [showReject, setShowReject] = useState(false);
    const [rejectNotes, setRejectNotes] = useState('');
    const [showQuestions, setShowQuestions] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [loadingQ, setLoadingQ] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [savingId, setSavingId] = useState(null);
    const reviewId = review.review_id;

    const fetchQuestions = async () => {
        if (questions.length > 0) { setShowQuestions(v => !v); return; }
        setShowQuestions(true); setLoadingQ(true);
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('examToken');
            const res = await api.get(`/tenant/${tenantSlug}/exam/projects/${review.project_id}/qbank/questions`, { headers: { Authorization: `Bearer ${token}` } });
            setQuestions(Array.isArray(res.data) ? res.data : []);
        } catch { setQuestions([]); } finally { setLoadingQ(false); }
    };
    const startEdit = (q) => { setEditingId(q.id); setEditForm({ model_answer: q.model_answer || '', marking_scheme: q.marking_scheme || '' }); };
    const saveEdit = async (q) => {
        setSavingId(q.id);
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('examToken');
            const res = await api.patch(`/tenant/${tenantSlug}/exam/projects/${review.project_id}/qbank/questions/${q.id}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
            setQuestions(qs => qs.map(item => item.id === q.id ? { ...item, ...res.data } : item));
            setEditingId(null);
        } catch { } finally { setSavingId(null); }
    };
    const DESCRIPTIVE = ['short_answer', 'long_answer', 'case_study', 'programming', 'numerical'];
    return (
        <div className="border rounded mb-2 bg-white" style={{ borderLeft: '3px solid #ffc107', overflow: 'hidden' }}>
            <div className="p-2">
                <div className="d-flex align-items-start justify-content-between gap-2">
                    <div style={{ minWidth: 0 }}>
                        <div className="fw-bold text-truncate" style={{ fontSize: 12 }}>{review.project_title || `Project #${review.project_id}`}</div>
                        {review.course_name && <div className="text-muted text-truncate" style={{ fontSize: 10 }}>{review.course_name}</div>}
                        <div className="text-muted" style={{ fontSize: 10 }}>{review.submitted_at ? new Date(review.submitted_at).toLocaleDateString() : '—'}{review.question_count ? ` · ${review.question_count} questions` : ''}</div>
                        {review.notes && <div className="text-secondary mt-1" style={{ fontSize: 11, fontStyle: 'italic' }}>{review.notes}</div>}
                    </div>
                    <span className="badge bg-warning text-dark" style={{ fontSize: 9, flexShrink: 0 }}>Pending</span>
                </div>
                <div className="d-flex gap-1 mt-2 flex-wrap">
                    <button className="btn btn-sm" style={{ fontSize: 10, background: '#d1e7dd', color: '#0f5132', border: 'none', flexShrink: 0 }} onClick={() => onApprove(reviewId)} disabled={busy}>✓ Approve</button>
                    <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 10, flexShrink: 0 }} onClick={() => { setShowReject(v => !v); setShowQuestions(false); }}>✕ Reject</button>
                    <button className="btn btn-sm btn-outline-primary" style={{ fontSize: 10, flexShrink: 0 }} onClick={fetchQuestions}>{showQuestions ? '▲ Hide Qs' : '📋 Review Qs'}</button>
                </div>
            </div>
            {showReject && (
                <div className="p-2 border-top bg-light">
                    <textarea className="form-control form-control-sm mb-1" rows={2} placeholder="Reason (optional)…" value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} style={{ fontSize: 11 }} />
                    <button className="btn btn-sm btn-outline-danger w-100" style={{ fontSize: 11 }} onClick={() => { onReject(reviewId, rejectNotes); setShowReject(false); }} disabled={busy}>Confirm Rejection</button>
                </div>
            )}
            {showQuestions && (
                <div className="border-top" style={{ maxHeight: 420, overflowY: 'auto', background: '#f8f9fa' }}>
                    {loadingQ ? <div className="text-center py-3"><Spinner size="sm" aria-hidden="true" /></div> : questions.length === 0 ? (
                        <div className="text-center text-muted py-2" style={{ fontSize: 11 }}>No questions found.</div>
                    ) : (
                        <div className="p-2" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ fontSize: 10, color: '#6c757d', marginBottom: 2 }}>{questions.length} questions · {questions.filter(q => DESCRIPTIVE.includes(q.question_type)).length} with model answers</div>
                            {questions.map((q, idx) => (
                                <div key={q.id} className="bg-white border rounded p-2" style={{ fontSize: 11 }}>
                                    <div className="d-flex align-items-start justify-content-between gap-1 mb-1">
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <span className="badge bg-secondary me-1" style={{ fontSize: 9 }}>{q.question_type?.replace('_', ' ') || 'Q'}</span>
                                            {q.marks && <span className="badge bg-light text-dark border me-1" style={{ fontSize: 9 }}>{q.marks}M</span>}
                                            {q.review_status === 'approved' && <span className="badge bg-success" style={{ fontSize: 9 }}>✓</span>}
                                        </div>
                                        <span style={{ fontSize: 10, color: '#6c757d', flexShrink: 0 }}>#{idx + 1}</span>
                                    </div>
                                    <div style={{ fontSize: 11, color: '#212529', marginBottom: 4, lineHeight: 1.4 }}>{(q.question_text || '').length > 120 ? `${q.question_text.slice(0, 120)}…` : q.question_text}</div>
                                    {(q.mapped_cos?.length > 0 || q.mapped_los?.length > 0) && (
                                        <div style={{ fontSize: 10, color: '#0d6efd', marginBottom: 4 }}>
                                            {q.mapped_cos?.length > 0 && `CO: ${q.mapped_cos.join(', ')}`}
                                            {q.mapped_cos?.length > 0 && q.mapped_los?.length > 0 && '  '}
                                            {q.mapped_los?.length > 0 && `LO: ${q.mapped_los.join(', ')}`}
                                        </div>
                                    )}
                                    {DESCRIPTIVE.includes(q.question_type) && (
                                        editingId === q.id ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                                                <label style={{ fontSize: 10, fontWeight: 600, color: '#6c757d' }}>Model Answer</label>
                                                <textarea className="form-control form-control-sm" rows={2} style={{ fontSize: 10 }} value={editForm.model_answer} onChange={e => setEditForm(f => ({ ...f, model_answer: e.target.value }))} />
                                                <label style={{ fontSize: 10, fontWeight: 600, color: '#6c757d' }}>Marking Scheme</label>
                                                <textarea className="form-control form-control-sm" rows={2} style={{ fontSize: 10 }} value={editForm.marking_scheme} onChange={e => setEditForm(f => ({ ...f, marking_scheme: e.target.value }))} />
                                                <div className="d-flex gap-1">
                                                    <button className="btn btn-sm btn-primary" style={{ fontSize: 10 }} onClick={() => saveEdit(q)} disabled={savingId === q.id}>{savingId === q.id ? '…' : 'Save'}</button>
                                                    <button className="btn btn-sm btn-light" style={{ fontSize: 10 }} onClick={() => setEditingId(null)}>Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ marginTop: 4 }}>
                                                {q.model_answer && <div style={{ fontSize: 10, color: '#6c757d', marginBottom: 2 }}><strong>Answer:</strong> {q.model_answer.length > 80 ? `${q.model_answer.slice(0, 80)}…` : q.model_answer}</div>}
                                                {q.marking_scheme && <div style={{ fontSize: 10, color: '#6c757d', marginBottom: 2 }}><strong>Scheme:</strong> {q.marking_scheme.length > 80 ? `${q.marking_scheme.slice(0, 80)}…` : q.marking_scheme}</div>}
                                                <button className="btn btn-sm btn-link p-0" style={{ fontSize: 10 }} onClick={() => startEdit(q)}>✏️ Edit Answer/Scheme</button>
                                            </div>
                                        )
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Markdown Result Viewer (shared by Lesson, Lab, Content workspaces) ───────
function MarkdownResultPanel({ content, loading, onOpenInEditor, onSave, saveLabel = 'Save to Materials', title = '' }) {
    const handleDownload = () => {
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(title || 'document').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
        a.click();
        URL.revokeObjectURL(url);
    };
    if (loading) return (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <div style={{ fontSize: 13 }}>Generating with AI… this may take 20–40 seconds</div>
        </div>
    );
    if (!content) return null;
    return (
        <div className="d-flex flex-column h-100">
            <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-white flex-shrink-0">
                <span className="fw-bold text-success" style={{ fontSize: 12 }}>✓ Generated</span>
                <div className="ms-auto d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 12 }} onClick={handleDownload}><Download size={13} className="me-1" />Download .md</button>
                    {onOpenInEditor && <button className="btn btn-sm btn-outline-primary" style={{ fontSize: 12 }} onClick={() => onOpenInEditor(content)}><BookOpenCheck size={13} className="me-1" />Open in Editor</button>}
                    {onSave && <button className="btn btn-sm btn-success" style={{ fontSize: 12 }} onClick={() => onSave(content)}><FileText size={13} className="me-1" />{saveLabel}</button>}
                </div>
            </div>
            <div className="flex-grow-1 overflow-auto p-3" style={{ background: '#fafafa', fontSize: 13, lineHeight: 1.7 }}>
                <ReactMarkdown>{content}</ReactMarkdown>
            </div>
        </div>
    );
}

// ─── Live Clock ───────────────────────────────────────────────────────────────
function LiveClock() {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);
    const pad = n => String(n).padStart(2, '0');
    const h = now.getHours(), m = now.getMinutes(), s = now.getSeconds();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return (
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', letterSpacing: 2, fontFamily: 'monospace' }}>
                {pad(h12)}:{pad(m)}:{pad(s)} <span style={{ fontSize: 13, fontWeight: 400 }}>{ampm}</span>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>
                {days[now.getDay()]}, {months[now.getMonth()]} {now.getDate()}, {now.getFullYear()}
            </div>
        </div>
    );
}

// ─── Mini Calendar ────────────────────────────────────────────────────────────
function MiniCalendar() {
    const [cursor, setCursor] = useState(new Date());
    const today = new Date();
    const y = cursor.getFullYear(), mo = cursor.getMonth();
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const firstDay = new Date(y, mo, 1).getDay();
    const daysInMonth = new Date(y, mo + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const prev = () => setCursor(new Date(y, mo - 1, 1));
    const next = () => setCursor(new Date(y, mo + 1, 1));
    const isToday = d => d && y === today.getFullYear() && mo === today.getMonth() && d === today.getDate();

    return (
        <div style={{ background: '#fff', borderRadius: 14, padding: '16px 14px', boxShadow: '0 2px 12px rgba(0,0,0,.07)' }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <button onClick={prev} className="btn btn-sm p-0 border-0 text-muted" style={{ lineHeight: 1 }}>&#8249;</button>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{months[mo]} {y}</span>
                <button onClick={next} className="btn btn-sm p-0 border-0 text-muted" style={{ lineHeight: 1 }}>&#8250;</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, textAlign: 'center' }}>
                {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                    <div key={d} style={{ fontSize: 9, fontWeight: 700, color: '#adb5bd', padding: '2px 0' }}>{d}</div>
                ))}
                {cells.map((d, i) => (
                    <div key={i} style={{
                        fontSize: 11, padding: '4px 0', borderRadius: 6, cursor: d ? 'default' : 'default',
                        background: isToday(d) ? '#6366f1' : 'transparent',
                        color: isToday(d) ? '#fff' : d ? '#1a1a2e' : 'transparent',
                        fontWeight: isToday(d) ? 700 : 400,
                    }}>{d || ''}</div>
                ))}
            </div>
        </div>
    );
}

// ─── Faculty Avatar ───────────────────────────────────────────────────────────
function FacultyAvatar({ name = '', size = 56, photoUrl = null, onClick }) {
    const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'FA';
    const colours = ['#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444'];
    const idx = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % colours.length;
    const baseStyle = {
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        border: '3px solid rgba(255,255,255,.25)', cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden', position: 'relative',
    };
    if (photoUrl) {
        return (
            <div style={baseStyle} onClick={onClick}>
                <img src={photoUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
        );
    }
    return (
        <div style={{ ...baseStyle, background: colours[idx], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.35, fontWeight: 700, color: '#fff' }}
            onClick={onClick}>{initials}</div>
    );
}

// ─── Profile Settings Modal ───────────────────────────────────────────────────
function ProfileSettingsModal({ show, onHide, tenantSlug, profile, onProfileUpdated }) {
    const [displayName, setDisplayName] = useState('');
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoB64, setPhotoB64] = useState(null);
    const [curPwd, setCurPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const fileRef = useRef();

    useEffect(() => {
        if (show && profile) {
            setDisplayName(profile.display_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim());
            setPhotoPreview(profile.profile_photo || null);
            setPhotoB64(null); setCurPwd(''); setNewPwd(''); setConfirmPwd('');
            setError(''); setSuccess('');
        }
    }, [show, profile]);

    const handlePhotoFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const MAX = 160;
                const scale = Math.min(MAX / img.width, MAX / img.height, 1);
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const b64 = canvas.toDataURL('image/jpeg', 0.82);
                setPhotoPreview(b64);
                setPhotoB64(b64);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setError(''); setSuccess('');
        if (newPwd && newPwd !== confirmPwd) { setError('New passwords do not match.'); return; }
        if (newPwd && newPwd.length < 6) { setError('Password must be at least 6 characters.'); return; }
        setSaving(true);
        try {
            const payload = {};
            if (displayName.trim()) payload.display_name = displayName.trim();
            if (photoB64 !== null) payload.profile_photo = photoB64;
            if (newPwd) { payload.current_password = curPwd; payload.new_password = newPwd; }
            const updated = await updateCopilotProfile(tenantSlug, payload);
            setSuccess('Profile updated successfully.');
            onProfileUpdated(updated);
            setCurPwd(''); setNewPwd(''); setConfirmPwd(''); setPhotoB64(null);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to update profile.');
        } finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton style={{ borderBottom: '1px solid #f1f5f9' }}>
                <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>Profile Settings</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 20 }}>
                {error && <Alert variant="danger" style={{ fontSize: 12, padding: '8px 12px' }}>{error}</Alert>}
                {success && <Alert variant="success" style={{ fontSize: 12, padding: '8px 12px' }}>{success}</Alert>}

                {/* Photo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', border: '3px solid #e9ecef', background: '#f1f5f9', flexShrink: 0 }}>
                        {photoPreview
                            ? <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#94a3b8' }}>👤</div>
                        }
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Profile Photo</div>
                        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }} onClick={() => fileRef.current?.click()}>Upload Photo</button>
                        {photoPreview && <button className="btn btn-sm btn-link text-danger ms-2" style={{ fontSize: 11 }} onClick={() => { setPhotoPreview(null); setPhotoB64(''); }}>Remove</button>}
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>JPG/PNG · auto-resized to 160×160</div>
                        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handlePhotoFile(e.target.files[0])} />
                    </div>
                </div>

                {/* Display name */}
                <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Display Name</Form.Label>
                    <Form.Control size="sm" value={displayName} onChange={e => setDisplayName(e.target.value)} style={{ fontSize: 12 }} />
                </Form.Group>

                <hr style={{ margin: '14px 0', borderColor: '#f1f5f9' }} />
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>Change Password</div>
                <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: 11, color: '#64748b' }}>Current Password</Form.Label>
                    <Form.Control type="password" size="sm" value={curPwd} onChange={e => setCurPwd(e.target.value)} style={{ fontSize: 12 }} autoComplete="current-password" />
                </Form.Group>
                <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: 11, color: '#64748b' }}>New Password</Form.Label>
                    <Form.Control type="password" size="sm" value={newPwd} onChange={e => setNewPwd(e.target.value)} style={{ fontSize: 12 }} autoComplete="new-password" />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 11, color: '#64748b' }}>Confirm New Password</Form.Label>
                    <Form.Control type="password" size="sm" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} style={{ fontSize: 12 }} autoComplete="new-password" />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: '1px solid #f1f5f9' }}>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <><Spinner size="sm" animation="border" className="me-1" aria-hidden="true" />Saving…</> : 'Save Changes'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

// ─── Q.Bank Workspace (full-width review panel) ───────────────────────────────
function QBankWorkspace({ tenantSlug, reviews, loading, onApprove, onReject, onRefresh }) {
    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#fff', borderBottom: '1px solid #e9ecef', padding: '10px 16px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <BookOpenCheck size={16} color="#6366f1" />
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Q.Bank Reviews</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{reviews.length} review{reviews.length !== 1 ? 's' : ''} · {reviews.filter(r => r.status === 'pending').length} pending</div>
                </div>
                <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" style={{ fontSize: 11 }} onClick={onRefresh}>
                    <RefreshCw size={12} /> Refresh
                </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8faff' }}>
                {loading && <div className="text-center py-5"><Spinner animation="border" size="sm" aria-hidden="true" /></div>}
                {!loading && reviews.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>All reviews are up to date</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>No pending Q.Bank reviews for your courses.</div>
                    </div>
                )}
                {!loading && reviews.length > 0 && (
                    <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {reviews.map(r => (
                            <QBankReviewCard key={r.review_id} review={r} tenantSlug={tenantSlug} onApprove={onApprove} onReject={onReject} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Copilot Footer ───────────────────────────────────────────────────────────
function CopilotFooter() {
    return (
        <div style={{ flexShrink: 0, borderTop: '1px solid #e9ecef', background: '#fff', padding: '8px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#94a3b8' }}>
            <span>© {new Date().getFullYear()} Teaching Copilot · AI drafts content — Faculty reviews, edits, and approves.</span>
            <div style={{ display: 'flex', gap: 16 }}>
                {[{ label: 'Terms of Use', href: '/terms' }, { label: 'Privacy Policy', href: '/privacy' }, { label: 'AI Ethics Policy', href: '/ai-ethics-policy' }].map(({ label, href }) => (
                    <a key={href} href={href} style={{ color: '#94a3b8', textDecoration: 'none' }} target="_blank" rel="noreferrer"
                        onMouseEnter={e => e.target.style.color = '#6366f1'} onMouseLeave={e => e.target.style.color = '#94a3b8'}>
                        {label}
                    </a>
                ))}
            </div>
        </div>
    );
}

// ─── Dashboard (no topic selected) ────────────────────────────────────────────
function CopilotDashboard({ projects, qbankReviews, onSelectTopic, onViewAnalytics, displayName: propDisplayName }) {
    const { user } = useSelector(s => s.user || {});
    const displayName = propDisplayName || user?.name || user?.email?.split('@')[0] || 'Faculty';
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

    const pendingReviews = (qbankReviews || []).filter(r => r.status === 'pending').length;
    const totalCourses = projects.length;

    const features = [
        { icon: '💬', color: '#6366f1', bg: 'linear-gradient(135deg,#6366f1,#8b5cf6)', title: 'AI Chat', desc: 'Curriculum-grounded teaching assistant with voice support' },
        { icon: '📄', color: '#0891b2', bg: 'linear-gradient(135deg,#0891b2,#06b6d4)', title: 'Content Studio', desc: 'Upload slides & notes → generate lecture notes or case studies' },
        { icon: '🗓️', color: '#059669', bg: 'linear-gradient(135deg,#059669,#10b981)', title: 'Lesson Planner', desc: 'Session-by-session plan from your curriculum draft' },
        { icon: '🔬', color: '#d97706', bg: 'linear-gradient(135deg,#d97706,#f59e0b)', title: 'Lab Designer', desc: 'Design lab sessions, assignments, simulations & fieldwork' },
        { icon: '🏆', color: '#dc2626', bg: 'linear-gradient(135deg,#dc2626,#ef4444)', title: 'Quiz Studio', desc: 'Live MCQ quizzes with leaderboard, timer & passcode' },
        { icon: '🚀', color: '#0284c7', bg: 'linear-gradient(135deg,#0284c7,#0ea5e9)', title: 'Push to LMS', desc: 'Push materials as DOCX to Moodle & Google Classroom' },
    ];

    return (
        <div className="h-100 overflow-auto" style={{ background: 'linear-gradient(160deg,#f8faff 0%,#f0f4ff 100%)', padding: '28px 32px' }}>
            {/* Greeting row */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>{greeting}, {displayName.split(' ')[0]} 👋</div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Here's your teaching workspace for today.</div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Your role</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', textTransform: 'capitalize' }}>{(user?.role || 'faculty').replace('_', ' ')}</div>
                    </div>
                    <FacultyAvatar name={displayName} size={44} />
                </div>
            </div>

            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 28 }}>
                {[
                    { label: 'Assigned Courses', value: totalCourses, icon: '📚', color: '#6366f1', bg: '#f0f0ff' },
                    { label: 'Q.Bank Pending', value: pendingReviews, icon: '📝', color: pendingReviews > 0 ? '#dc2626' : '#10b981', bg: pendingReviews > 0 ? '#fff1f1' : '#f0fdf4' },
                    { label: 'AI Tools Ready', value: features.length, icon: '⚡', color: '#0891b2', bg: '#f0fbff' },
                ].map((s, i) => (
                    <div key={i} style={{ background: s.bg, borderRadius: 14, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 1px 6px rgba(0,0,0,.06)' }}>
                        <div style={{ fontSize: 28 }}>{s.icon}</div>
                        <div>
                            <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Your Courses */}
            {projects.length > 0 && (
                <div className="mb-4">
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>Your Courses</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 12 }}>
                        {projects.map((p, i) => {
                            const hues = ['#6366f1','#0891b2','#059669','#d97706','#dc2626','#7c3aed'];
                            const c = hues[i % hues.length];
                            return (
                                <div key={p.id} style={{ background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 1px 8px rgba(0,0,0,.07)', borderLeft: `4px solid ${c}`, cursor: 'pointer' }}
                                    onClick={() => onViewAnalytics && onViewAnalytics(p)}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{p.name}</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>Click to view analytics</div>
                                    <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                                        <span style={{ fontSize: 10, background: '#f0f0ff', color: c, borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>Course</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Tools grid */}
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>Copilot Tools</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                {features.map((f, i) => (
                    <div key={i} style={{ background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 1px 8px rgba(0,0,0,.07)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{f.icon}</div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 3 }}>{f.title}</div>
                            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>{f.desc}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 24 }}>
                Select a topic from the sidebar to start using any tool.
            </div>
        </div>
    );
}

// ─── Content Studio Workspace ─────────────────────────────────────────────────
function ContentStudioWorkspace({ tenantSlug, project, topic, onOpenInEditor }) {
    const [file, setFile] = useState(null);
    const [contentType, setContentType] = useState('notes');
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef();

    const handleFile = (f) => { setFile(f); setTitle(t => t || f.name.replace(/\.[^.]+$/, '')); };
    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); };

    const handleGenerate = async () => {
        if (!file) return;
        setLoading(true); setResult('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('content_type', contentType);
            formData.append('title', title);
            formData.append('concept_id', topic.id);
            const data = await uploadCopilotMaterial(tenantSlug, project.id, formData);
            setResult(data.content || '');
        } catch (e) {
            showToast('Generation failed. Please try again.', 'error');
        } finally { setLoading(false); }
    };

    return (
        <div className="h-100 d-flex flex-column">
            <div className="p-3 border-bottom bg-white d-flex align-items-center gap-2 flex-shrink-0">
                <Upload size={18} color="#6f42c1" />
                <div>
                    <div className="fw-bold" style={{ fontSize: 14 }}>Content Studio</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Upload material → Generate lecture notes or case study</div>
                </div>
            </div>
            {result ? (
                <MarkdownResultPanel content={result} loading={false} onOpenInEditor={onOpenInEditor} title={title || topic.title} />
            ) : (
                <div className="flex-grow-1 overflow-auto p-4">
                    {/* Drop zone */}
                    <div
                        onClick={() => fileRef.current?.click()}
                        onDrop={handleDrop}
                        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        style={{
                            border: `2px dashed ${dragOver ? '#0d6efd' : '#ced4da'}`,
                            borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer',
                            background: dragOver ? '#f0f5ff' : '#fafafa', transition: 'all .2s',
                            marginBottom: 20,
                        }}
                    >
                        <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.md" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
                        <Upload size={32} color="#6c757d" style={{ marginBottom: 10 }} />
                        {file ? (
                            <div><div className="fw-bold" style={{ fontSize: 14, color: '#0d6efd' }}>{file.name}</div><div className="text-muted" style={{ fontSize: 12 }}>{(file.size / 1024).toFixed(0)} KB · Click to change</div></div>
                        ) : (
                            <div><div className="fw-bold" style={{ fontSize: 14 }}>Drop a file here or click to browse</div><div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>PDF, DOCX, TXT, Markdown</div></div>
                        )}
                    </div>

                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Generate As</Form.Label>
                                <div className="d-flex gap-2">
                                    {[['notes', 'Lecture Notes', BookOpenCheck], ['case_study', 'Case Study', FileText]].map(([v, label, Icon]) => (
                                        <div key={v} onClick={() => setContentType(v)} style={{
                                            flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                                            border: `2px solid ${contentType === v ? '#6f42c1' : '#dee2e6'}`,
                                            background: contentType === v ? '#f3ecff' : '#fff',
                                        }}>
                                            <Icon size={16} color={contentType === v ? '#6f42c1' : '#6c757d'} style={{ marginBottom: 4 }} />
                                            <div style={{ fontSize: 12, fontWeight: contentType === v ? 700 : 400, color: contentType === v ? '#6f42c1' : '#333' }}>{label}</div>
                                        </div>
                                    ))}
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Document Title (optional)</Form.Label>
                                <Form.Control type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={topic.title} style={{ fontSize: 13 }} />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Button variant="primary" onClick={handleGenerate} disabled={!file || loading} style={{ minWidth: 180 }}>
                        {loading ? <><Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />Generating…</> : <><Sparkles size={15} className="me-2" />Generate Content</>}
                    </Button>
                </div>
            )}
            {result && (
                <div className="p-2 border-top bg-white flex-shrink-0">
                    <button className="btn btn-sm btn-link" onClick={() => setResult('')}><ArrowLeft size={13} className="me-1" />New Upload</button>
                </div>
            )}
        </div>
    );
}

// ─── Lesson Planner Workspace ─────────────────────────────────────────────────
function LessonPlannerWorkspace({ tenantSlug, project, topic, onOpenInEditor }) {
    const [scope, setScope] = useState('topic'); // 'course' | 'topic'
    const [durationType, setDurationType] = useState('weeks');
    const [durationValue, setDurationValue] = useState(4);
    const [credits, setCredits] = useState('');
    const [weekdaysOnly, setWeekdaysOnly] = useState(true);
    const [instructions, setInstructions] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    const subjectLabel = scope === 'course' ? (project.name || 'Entire Course') : (topic.title || topic.concept_name);

    const handleGenerate = async () => {
        setLoading(true); setResult('');
        try {
            const payload = {
                duration_type: durationType,
                duration_value: Number(durationValue),
                credits: credits ? Number(credits) : null,
                weekdays_only: weekdaysOnly,
                additional_instructions: instructions,
            };
            let data;
            if (scope === 'course') {
                data = await generateCourseLessonPlan(tenantSlug, project.id, {
                    ...payload, scope: 'course', course_name: project.name || '',
                });
            } else {
                data = await generateLessonPlan(tenantSlug, project.id, topic.id, {
                    ...payload, scope: 'topic',
                });
            }
            setResult(data.content || '');
        } catch { showToast('Lesson plan generation failed.', 'error'); }
        finally { setLoading(false); }
    };

    return (
        <div className="h-100 d-flex flex-column">
            <div className="p-3 border-bottom bg-white d-flex align-items-center gap-2 flex-shrink-0">
                <Calendar size={18} color="#198754" />
                <div>
                    <div className="fw-bold" style={{ fontSize: 14 }}>Lesson Planner</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                        {scope === 'course'
                            ? <>Full course plan for: <strong>{project.name}</strong></>
                            : <>Topic plan for: <strong>{topic.title || topic.concept_name}</strong></>}
                    </div>
                </div>
            </div>

            {result ? (
                <>
                    <MarkdownResultPanel content={result} loading={false} onOpenInEditor={onOpenInEditor} title={`Lesson Plan - ${subjectLabel}`} />
                    <div className="p-2 border-top bg-white flex-shrink-0">
                        <button className="btn btn-sm btn-link" onClick={() => setResult('')}><ArrowLeft size={13} className="me-1" />New Plan</button>
                    </div>
                </>
            ) : (
                <div className="flex-grow-1 overflow-auto p-4">
                    {/* Scope selector */}
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold d-block mb-2" style={{ fontSize: 12 }}>Plan Scope</Form.Label>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {[['topic', 'Specific Topic', topic.title || topic.concept_name], ['course', 'Entire Course', project.name]].map(([v, label, sub]) => (
                                <div key={v} onClick={() => setScope(v)} style={{
                                    flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                                    border: `2px solid ${scope === v ? '#198754' : '#dee2e6'}`,
                                    background: scope === v ? '#f0fff4' : '#fff',
                                }}>
                                    <div style={{ fontSize: 12, fontWeight: scope === v ? 700 : 400, color: scope === v ? '#198754' : '#333' }}>{label}</div>
                                    <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>
                                </div>
                            ))}
                        </div>
                    </Form.Group>

                    <Row className="g-3 mb-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Schedule Type</Form.Label>
                                <Form.Select value={durationType} onChange={e => setDurationType(e.target.value)} style={{ fontSize: 13 }}>
                                    <option value="hours">Contact Hours</option>
                                    <option value="days">Days</option>
                                    <option value="weeks">Weeks</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>
                                    {durationType === 'hours' ? 'Total Hours' : durationType === 'days' ? 'Number of Days' : 'Number of Weeks'}
                                </Form.Label>
                                <Form.Control type="number" min={1} max={52} value={durationValue} onChange={e => setDurationValue(e.target.value)} style={{ fontSize: 13 }} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Academic Credits <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                <Form.Control type="number" min={1} max={10} value={credits} onChange={e => setCredits(e.target.value)} placeholder="e.g. 3" style={{ fontSize: 13 }} />
                            </Form.Group>
                        </Col>
                    </Row>

                    {durationType !== 'hours' && (
                        <Form.Check className="mb-3" type="switch" id="weekdays-switch" label={<span style={{ fontSize: 13 }}>Count weekdays only (Mon–Fri, skip Sat/Sun)</span>} checked={weekdaysOnly} onChange={e => setWeekdaysOnly(e.target.checked)} />
                    )}

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Special Instructions <span className="text-muted fw-normal">(optional)</span></Form.Label>
                        <Form.Control as="textarea" rows={3} value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="e.g. Include flipped classroom sessions, add weekly assessments, emphasise clinical application…" style={{ fontSize: 13 }} />
                    </Form.Group>

                    {loading ? (
                        <div className="d-flex align-items-center gap-3 py-2">
                            <Spinner animation="border" size="sm" variant="success" />
                            <span style={{ fontSize: 13, color: '#198754' }}>Building your lesson plan with AI…</span>
                        </div>
                    ) : (
                        <Button variant="success" onClick={handleGenerate} style={{ minWidth: 180 }}>
                            <Calendar size={15} className="me-2" />Generate Lesson Plan
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Lab Designer Workspace ───────────────────────────────────────────────────
function LabDesignerWorkspace({ tenantSlug, project, topic, onOpenInEditor }) {
    const [labType, setLabType] = useState('in_lab');
    const [durationHours, setDurationHours] = useState(2);
    const [instructions, setInstructions] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    const labTypes = [
        { key: 'in_lab', label: 'In-Lab Session', emoji: '🔬' },
        { key: 'assignment', label: 'Assignment', emoji: '📝' },
        { key: 'field_work', label: 'Field Work', emoji: '🌍' },
        { key: 'simulation', label: 'Simulation', emoji: '💻' },
        { key: 'workshop', label: 'Workshop', emoji: '🛠️' },
    ];

    const handleGenerate = async () => {
        setLoading(true); setResult('');
        try {
            const data = await generateLabSession(tenantSlug, project.id, topic.id, {
                lab_type: labType,
                duration_hours: Number(durationHours),
                additional_instructions: instructions,
            });
            setResult(data.content || '');
        } catch { showToast('Lab session generation failed.', 'error'); }
        finally { setLoading(false); }
    };

    return (
        <div className="h-100 d-flex flex-column">
            <div className="p-3 border-bottom bg-white d-flex align-items-center gap-2 flex-shrink-0">
                <FlaskConical size={18} color="#fd7e14" />
                <div>
                    <div className="fw-bold" style={{ fontSize: 14 }}>Lab Designer</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Design practical sessions for: <strong>{topic.title}</strong></div>
                </div>
            </div>

            {result ? (
                <>
                    <MarkdownResultPanel content={result} loading={false} onOpenInEditor={onOpenInEditor} title={`Lab Session - ${topic.title}`} />
                    <div className="p-2 border-top bg-white flex-shrink-0">
                        <button className="btn btn-sm btn-link" onClick={() => setResult('')}><ArrowLeft size={13} className="me-1" />New Session</button>
                    </div>
                </>
            ) : (
                <div className="flex-grow-1 overflow-auto p-4">
                    <Form.Label className="fw-bold d-block mb-2" style={{ fontSize: 12 }}>Session Type</Form.Label>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
                        {labTypes.map(lt => (
                            <div key={lt.key} onClick={() => setLabType(lt.key)} style={{
                                padding: '10px 16px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                                border: `2px solid ${labType === lt.key ? '#fd7e14' : '#dee2e6'}`,
                                background: labType === lt.key ? '#fff3e0' : '#fff',
                                transition: 'all .15s',
                            }}>
                                <div style={{ fontSize: 20, marginBottom: 4 }}>{lt.emoji}</div>
                                <div style={{ fontSize: 11, fontWeight: labType === lt.key ? 700 : 400, color: labType === lt.key ? '#fd7e14' : '#333' }}>{lt.label}</div>
                            </div>
                        ))}
                    </div>

                    <Row className="g-3 mb-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Duration (hours)</Form.Label>
                                <Form.Select value={durationHours} onChange={e => setDurationHours(e.target.value)} style={{ fontSize: 13 }}>
                                    {[1, 1.5, 2, 2.5, 3, 4, 6, 8].map(h => <option key={h} value={h}>{h} {h === 1 ? 'hour' : 'hours'}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Special Instructions <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                <Form.Control as="textarea" rows={2} value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="e.g. Include safety protocols, add troubleshooting guide, use open-source tools only…" style={{ fontSize: 13 }} />
                            </Form.Group>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="d-flex align-items-center gap-3 py-2">
                            <Spinner animation="border" size="sm" style={{ color: '#fd7e14' }} />
                            <span style={{ fontSize: 13, color: '#fd7e14' }}>Designing lab session with AI…</span>
                        </div>
                    ) : (
                        <Button onClick={handleGenerate} style={{ minWidth: 180, background: '#fd7e14', border: 'none' }}>
                            <FlaskConical size={15} className="me-2" />Design Lab Session
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Quiz Classroom Push Modal ────────────────────────────────────────────────
const QUIZ_CLASSROOM_SCOPE = [
    'https://www.googleapis.com/auth/classroom.courses.readonly',
    'https://www.googleapis.com/auth/classroom.announcements',
].join(' ');
const GOOGLE_CLIENT_ID_COPILOT = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function QuizClassroomModal({ tenantSlug, project, publishedId, quizTitle, passcode, onClose }) {
    const [step, setStep] = useState('auth'); // auth | courses | pushing | done | error
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const [error, setError] = useState('');
    const [resultUrl, setResultUrl] = useState('');
    const tokenClientRef = useRef(null);

    const initAndRequestToken = useCallback(() => {
        if (!GOOGLE_CLIENT_ID_COPILOT) {
            setError('Google Client ID (REACT_APP_GOOGLE_CLIENT_ID) is not configured.');
            setStep('error');
            return;
        }
        const doRequest = () => {
            tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
                client_id: GOOGLE_CLIENT_ID_COPILOT,
                scope: QUIZ_CLASSROOM_SCOPE,
                callback: async (response) => {
                    if (response.error) { setError(`Authorization failed: ${response.error}`); setStep('error'); return; }
                    const token = response.access_token;
                    setAccessToken(token);
                    setStep('courses');
                    try {
                        const data = await listCopilotClassroomCourses(token);
                        setCourses(data.courses || []);
                    } catch { setError('Failed to fetch your Google Classroom courses.'); setStep('error'); }
                },
            });
            tokenClientRef.current.requestAccessToken({ prompt: 'consent' });
        };
        if (window.google?.accounts?.oauth2) { doRequest(); return; }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.onload = doRequest;
        script.onerror = () => { setError('Failed to load Google Identity Services.'); setStep('error'); };
        document.head.appendChild(script);
    }, []);

    const handlePost = async () => {
        if (!selectedCourse) return;
        setStep('pushing');
        try {
            const res = await pushQuizLinkToClassroom(tenantSlug, project.id, {
                quiz_id: publishedId,
                access_token: accessToken,
                course_id: selectedCourse,
                title: quizTitle,
                app_base_url: window.location.origin,
            });
            setResultUrl(res.url || '');
            setStep('done');
        } catch (err) {
            setError(err?.response?.data?.detail || 'Failed to post to Google Classroom.');
            setStep('error');
        }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>Post Quiz Link to Google Classroom</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {step === 'auth' && (
                    <div className="text-center py-3">
                        <div style={{ fontSize: 36, marginBottom: 12 }}>🏫</div>
                        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                            This will post the quiz link as an announcement in your Google Classroom course.
                            Students click the link to open the quiz — no Google Form is created.
                        </p>
                        {passcode && (
                            <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                                The passcode <strong>{passcode}</strong> will be included in the announcement.
                            </p>
                        )}
                        <Button variant="danger" onClick={initAndRequestToken}>Sign in with Google</Button>
                    </div>
                )}
                {step === 'courses' && (
                    <div>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold" style={{ fontSize: 13 }}>Select Google Classroom Course</Form.Label>
                            {courses.length === 0 ? (
                                <p className="text-muted" style={{ fontSize: 13 }}>No active courses found.</p>
                            ) : (
                                <Form.Select value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)} style={{ fontSize: 13 }}>
                                    <option value="">— Choose a course —</option>
                                    {courses.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}{c.section ? ` · ${c.section}` : ''}</option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                        <Button variant="danger" onClick={handlePost} disabled={!selectedCourse} style={{ width: '100%' }}>
                            Post Announcement
                        </Button>
                    </div>
                )}
                {step === 'pushing' && (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="danger" className="mb-2" />
                        <div style={{ fontSize: 13, color: '#6c757d' }}>Posting to Google Classroom…</div>
                    </div>
                )}
                {step === 'done' && (
                    <div className="text-center py-3">
                        <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                        <div className="fw-bold mb-2">Announcement posted!</div>
                        <p className="text-muted" style={{ fontSize: 13 }}>
                            Students can now see the quiz link in their Classroom stream.
                        </p>
                        {resultUrl && (
                            <a href={resultUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                                View in Classroom ↗
                            </a>
                        )}
                        <br /><Button variant="outline-secondary" size="sm" className="mt-3" onClick={onClose}>Close</Button>
                    </div>
                )}
                {step === 'error' && (
                    <div className="text-center py-3">
                        <div style={{ fontSize: 36, marginBottom: 8 }}>❌</div>
                        <p className="text-danger" style={{ fontSize: 13 }}>{error}</p>
                        <Button variant="outline-secondary" size="sm" onClick={() => setStep('auth')}>Try Again</Button>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}

// ─── Quiz Studio Workspace ────────────────────────────────────────────────────
function QuizStudioWorkspace({ tenantSlug, project, topic }) {
    const [prompt, setPrompt] = useState('');
    const [numQuestions, setNumQuestions] = useState(10);
    const [questionType, setQuestionType] = useState('mcq');
    const [timerMinutes, setTimerMinutes] = useState(15);
    const [passcode, setPasscode] = useState('');
    const [loading, setLoading] = useState(false);
    const [quizData, setQuizData] = useState(null);
    const [standaloneHtml, setStandaloneHtml] = useState('');
    const [publishedId, setPublishedId] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showClassroomModal, setShowClassroomModal] = useState(false);

    const tenantName = project?.tenant_name || tenantSlug || 'AcademicOS';

    const handleGenerate = async () => {
        if (!prompt.trim()) { showToast('Please describe what the quiz should cover.', 'error'); return; }
        setLoading(true); setQuizData(null); setStandaloneHtml(''); setPublishedId('');
        try {
            const data = await generateQuiz(tenantSlug, project.id, topic.id, {
                prompt_text: prompt,
                num_questions: numQuestions,
                question_type: questionType,
                timer_minutes: timerMinutes,
            });
            setQuizData(data.quiz_data);
            setStandaloneHtml(data.standalone_html || '');
        } catch { showToast('Quiz generation failed. Try again.', 'error'); }
        finally { setLoading(false); }
    };

    const handlePublish = async () => {
        if (!quizData) return;
        setPublishing(true);
        try {
            const data = await publishQuiz({
                quiz_data: quizData,
                topic: topic.title,
                timer_minutes: timerMinutes,
                passcode: passcode.trim() || null,
                tenant_name: tenantName,
            });
            setPublishedId(data.quiz_id);
        } catch { showToast('Failed to publish quiz.', 'error'); }
        finally { setPublishing(false); }
    };


    const handleDownload = () => {
        if (!standaloneHtml) return;
        const blob = new Blob([standaloneHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quiz_${topic.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const shareUrl = publishedId ? `${window.location.origin}/quiz/${publishedId}` : '';

    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handlePreview = () => {
        if (!standaloneHtml) return;
        const w = window.open('', '_blank');
        w.document.write(standaloneHtml);
        w.document.close();
    };

    const qtypes = [
        { key: 'mcq', label: 'MCQ', desc: 'Multiple-choice questions with 4 options' },
        { key: 'flashcard', label: 'Flashcards', desc: 'Flip-card term & definition pairs' },
        { key: 'mixed', label: 'Mixed', desc: 'Both MCQs and flashcards' },
    ];

    return (
        <div className="h-100 d-flex flex-column">
            <div className="p-3 border-bottom bg-white d-flex align-items-center gap-2 flex-shrink-0">
                <Trophy size={18} color="#dc3545" />
                <div>
                    <div className="fw-bold" style={{ fontSize: 14 }}>Quiz Studio</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Instant quiz from: <strong>{topic.title}</strong></div>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto">
                <div className="p-4">
                    {/* Config form — always visible */}
                    <Row className="g-3 mb-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Quiz Focus / Instructions</Form.Label>
                                <Form.Control as="textarea" rows={2} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={`e.g. Focus on ${topic.title} fundamentals, definitions, and clinical applications…`} style={{ fontSize: 13 }} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Question Type</Form.Label>
                                <div className="d-flex flex-column gap-1">
                                    {qtypes.map(qt => (
                                        <label key={qt.key} className="d-flex align-items-start gap-2 p-2 rounded border" style={{ cursor: 'pointer', background: questionType === qt.key ? '#fff5f5' : '#fff', borderColor: questionType === qt.key ? '#dc3545' : '#dee2e6' }}>
                                            <input type="radio" value={qt.key} checked={questionType === qt.key} onChange={() => setQuestionType(qt.key)} style={{ marginTop: 2 }} />
                                            <div>
                                                <div style={{ fontSize: 12, fontWeight: 600 }}>{qt.label}</div>
                                                <div style={{ fontSize: 10, color: '#6c757d' }}>{qt.desc}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Number of Questions</Form.Label>
                                <Form.Select value={numQuestions} onChange={e => setNumQuestions(Number(e.target.value))} style={{ fontSize: 13 }}>
                                    {[5, 10, 15, 20, 25, 30].map(n => <option key={n} value={n}>{n} questions</option>)}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}><Clock size={13} className="me-1" />Timer</Form.Label>
                                <Form.Select value={timerMinutes} onChange={e => setTimerMinutes(Number(e.target.value))} style={{ fontSize: 13 }}>
                                    <option value={0}>No timer</option>
                                    {[5, 10, 15, 20, 30, 45, 60].map(m => <option key={m} value={m}>{m} minutes</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex flex-column justify-content-end">
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Passcode (optional)</Form.Label>
                                <Form.Control
                                    type="text" value={passcode} onChange={e => setPasscode(e.target.value)}
                                    placeholder="e.g. CLASS2025" maxLength={20}
                                    style={{ fontSize: 13, letterSpacing: 1 }}
                                />
                                <Form.Text className="text-muted" style={{ fontSize: 10 }}>Students must enter this before accessing the quiz</Form.Text>
                            </Form.Group>
                            <Button variant="danger" onClick={handleGenerate} disabled={loading || !prompt.trim()} style={{ width: '100%' }}>
                                {loading ? <><Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />Generating…</> : <><Trophy size={15} className="me-2" />Generate Quiz</>}
                            </Button>
                        </Col>
                    </Row>

                    {/* Results */}
                    {loading && (
                        <div className="text-center py-3 text-muted" style={{ fontSize: 13 }}>
                            <Spinner animation="border" size="sm" variant="danger" className="me-2" />
                            Building quiz questions with AI…
                        </div>
                    )}

                    {quizData && !loading && (
                        <div className="mt-2">
                            <div className="p-3 rounded-3 border mb-3" style={{ background: '#fff5f5', borderColor: '#f8d7da' }}>
                                <div className="fw-bold mb-1" style={{ fontSize: 13, color: '#842029' }}>✓ Quiz Ready: {quizData.title}</div>
                                <div style={{ fontSize: 12, color: '#6c757d' }}>
                                    {(quizData.questions || []).length} MCQ questions · {(quizData.cards || []).length} flashcards · {timerMinutes > 0 ? `${timerMinutes}-min timer` : 'No timer'}
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mb-4">
                                <Button variant="outline-secondary" size="sm" onClick={handlePreview}><MonitorPlay size={13} className="me-1" />Preview Quiz</Button>
                                <Button variant="outline-dark" size="sm" onClick={handleDownload}><Download size={13} className="me-1" />Download HTML</Button>
                                {!publishedId ? (
                                    <Button variant="outline-danger" size="sm" onClick={handlePublish} disabled={publishing}>
                                        {publishing ? <><Spinner size="sm" animation="border" className="me-1" aria-hidden="true" />Publishing…</> : <><Share2 size={13} className="me-1" />Share Live (with Leaderboard)</>}
                                    </Button>
                                ) : (
                                    <>
                                        <div className="d-flex align-items-center gap-2 px-3 py-1 rounded" style={{ background: '#d1e7dd', border: '1px solid #badbcc' }}>
                                            <Link size={13} color="#0f5132" />
                                            <span style={{ fontSize: 12, color: '#0f5132', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shareUrl}</span>
                                            <button className="btn btn-sm p-0 border-0 bg-transparent" onClick={handleCopy} title="Copy link">
                                                {copied ? <Check size={14} color="#0f5132" /> : <Copy size={14} color="#0f5132" />}
                                            </button>
                                            <a href={shareUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#0f5132' }}>Open ↗</a>
                                        </div>
                                        {passcode && (
                                            <span className="badge" style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', fontSize: 11 }}>
                                                🔒 Passcode: {passcode}
                                            </span>
                                        )}
                                        <Button variant="outline-primary" size="sm" onClick={() => setShowClassroomModal(true)}>
                                            <Send size={13} className="me-1" />Push to Classroom
                                        </Button>
                                        <a
                                            href={`/quiz/${publishedId}/monitor`}
                                            target="_blank" rel="noreferrer"
                                            className="btn btn-sm btn-outline-dark"
                                            style={{ fontSize: 12 }}
                                        >
                                            📊 Monitor
                                        </a>
                                    </>
                                )}
                            </div>

                            {/* Quick question preview */}
                            {(quizData.questions || []).length > 0 && (
                                <div>
                                    <div className="fw-bold mb-2" style={{ fontSize: 12, color: '#6c757d', textTransform: 'uppercase' }}>Preview — First 3 Questions</div>
                                    {quizData.questions.slice(0, 3).map((q, i) => (
                                        <div key={i} className="p-3 bg-white border rounded mb-2">
                                            <div className="fw-bold mb-2" style={{ fontSize: 12 }}>Q{i + 1}. {q.question}</div>
                                            <div className="d-flex flex-wrap gap-1">
                                                {(q.options || []).map((opt, j) => (
                                                    <span key={j} className="badge" style={{ fontSize: 10, background: j === q.correct ? '#d1e7dd' : '#f8f9fa', color: j === q.correct ? '#0f5132' : '#333', border: '1px solid #dee2e6' }}>{opt}</span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {quizData.questions.length > 3 && <div style={{ fontSize: 11, color: '#6c757d' }}>+{quizData.questions.length - 3} more questions…</div>}
                                </div>
                            )}
                            {(quizData.cards || []).length > 0 && (
                                <div className="mt-3">
                                    <div className="fw-bold mb-2" style={{ fontSize: 12, color: '#6c757d', textTransform: 'uppercase' }}>Flashcard Preview</div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {quizData.cards.slice(0, 4).map((c, i) => (
                                            <div key={i} className="p-2 bg-white border rounded" style={{ width: 160, fontSize: 11 }}>
                                                <div className="fw-bold mb-1" style={{ color: '#0d6efd' }}>{c.front}</div>
                                                <div className="text-muted">{c.back}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {showClassroomModal && (
                <QuizClassroomModal
                    tenantSlug={tenantSlug}
                    project={project}
                    publishedId={publishedId}
                    quizTitle={quizData?.title || topic.title}
                    passcode={passcode}
                    onClose={() => setShowClassroomModal(false)}
                />
            )}
        </div>
    );
}

// ─── Push to LMS Workspace ────────────────────────────────────────────────────
const MATERIAL_CLASSROOM_SCOPE = [
    'https://www.googleapis.com/auth/classroom.courses.readonly',
    'https://www.googleapis.com/auth/classroom.courseworkmaterials',
    'https://www.googleapis.com/auth/drive.file',
].join(' ');

function PushToLMSWorkspace({ tenantSlug, project, topic, savedMaterials }) {
    const [platform, setPlatform] = useState('moodle');
    const [selectedMaterial, setSelectedMaterial] = useState('');

    // Moodle state
    const [moodleCourses, setMoodleCourses] = useState([]);
    const [moodleCourseId, setMoodleCourseId] = useState('');
    const [loadingMoodleCourses, setLoadingMoodleCourses] = useState(false);

    // Classroom state
    const [classroomStep, setClassroomStep] = useState('idle'); // idle | auth | courses | pushing | done | error
    const [classroomCourses, setClassroomCourses] = useState([]);
    const [classroomCourseId, setClassroomCourseId] = useState('');
    const [classroomAccessToken, setClassroomAccessToken] = useState('');
    const [classroomError, setClassroomError] = useState('');
    const classroomTokenClientRef = useRef(null);

    // Shared state
    const [pushing, setPushing] = useState(false);
    const [pushed, setPushed] = useState(false);

    const loadMoodleCourses = async () => {
        setLoadingMoodleCourses(true);
        try {
            const res = await api.get(`/tenant/${tenantSlug}/exam/settings/moodle/courses`);
            setMoodleCourses(res.data?.courses || res.data || []);
        } catch { showToast('Could not load Moodle courses. Check Moodle integration in Exam Settings.', 'error'); }
        finally { setLoadingMoodleCourses(false); }
    };

    useEffect(() => { if (platform === 'moodle') loadMoodleCourses(); }, [platform]); // eslint-disable-line

    // ── Classroom OAuth ──────────────────────────────────────────────────────
    const initClassroomAuth = useCallback(() => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
        if (!clientId) {
            setClassroomError('Google Client ID (REACT_APP_GOOGLE_CLIENT_ID) is not configured.');
            setClassroomStep('error');
            return;
        }
        const doRequest = () => {
            classroomTokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope: MATERIAL_CLASSROOM_SCOPE,
                callback: async (response) => {
                    if (response.error) {
                        setClassroomError(`Authorization failed: ${response.error}`);
                        setClassroomStep('error');
                        return;
                    }
                    const token = response.access_token;
                    setClassroomAccessToken(token);
                    setClassroomStep('courses');
                    try {
                        const data = await listCopilotClassroomCourses(token);
                        setClassroomCourses(data.courses || []);
                    } catch {
                        setClassroomError('Failed to fetch your Google Classroom courses.');
                        setClassroomStep('error');
                    }
                },
            });
            classroomTokenClientRef.current.requestAccessToken({ prompt: 'consent' });
        };
        if (window.google?.accounts?.oauth2) { doRequest(); return; }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.onload = doRequest;
        script.onerror = () => { setClassroomError('Failed to load Google Identity Services.'); setClassroomStep('error'); };
        document.head.appendChild(script);
        setClassroomStep('auth');
    }, []);

    // ── Push handlers ────────────────────────────────────────────────────────
    const handleMoodlePush = async () => {
        if (!selectedMaterial || !moodleCourseId) { showToast('Select a material and course first.', 'error'); return; }
        setPushing(true);
        try {
            await pushMaterialToMoodle(tenantSlug, project.id, {
                material_id: Number(selectedMaterial),
                course_id: Number(moodleCourseId),
            });
            setPushed(true);
            showToast('Material posted to Moodle course announcements.', 'success');
        } catch (err) {
            showToast(err?.response?.data?.detail || 'Moodle push failed.', 'error');
        } finally { setPushing(false); }
    };

    const handleClassroomPush = async () => {
        if (!selectedMaterial || !classroomCourseId) { showToast('Select a material and course first.', 'error'); return; }
        setClassroomStep('pushing');
        try {
            await pushMaterialToClassroom(tenantSlug, project.id, {
                material_id: Number(selectedMaterial),
                course_id: classroomCourseId,
                access_token: classroomAccessToken,
            });
            setClassroomStep('done');
            showToast('Material posted to Google Classroom.', 'success');
        } catch (err) {
            setClassroomError(err?.response?.data?.detail || 'Google Classroom push failed.');
            setClassroomStep('error');
        }
    };

    const resetClassroom = () => { setClassroomStep('idle'); setClassroomCourseId(''); setClassroomError(''); setPushed(false); };

    return (
        <div className="h-100 d-flex flex-column">
            <div className="p-3 border-bottom bg-white d-flex align-items-center gap-2 flex-shrink-0">
                <Send size={18} color="#0dcaf0" />
                <div>
                    <div className="fw-bold" style={{ fontSize: 14 }}>Push to LMS</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>Deliver saved materials to Moodle or Google Classroom</div>
                </div>
            </div>
            <div className="flex-grow-1 overflow-auto p-4">

                {/* Platform selector */}
                <Form.Group className="mb-4">
                    <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Platform</Form.Label>
                    <div className="d-flex gap-2">
                        {[['moodle', 'Moodle'], ['classroom', 'Google Classroom']].map(([v, label]) => (
                            <div key={v} onClick={() => { setPlatform(v); setPushed(false); resetClassroom(); }} style={{
                                flex: 1, padding: '12px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                                border: `2px solid ${platform === v ? '#0dcaf0' : '#dee2e6'}`,
                                background: platform === v ? '#e8feff' : '#fff',
                            }}>
                                <div style={{ fontSize: 13, fontWeight: platform === v ? 700 : 400, color: platform === v ? '#0097a7' : '#333' }}>{label}</div>
                            </div>
                        ))}
                    </div>
                </Form.Group>

                {/* Material selector */}
                <Form.Group className="mb-4">
                    <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Material to Push</Form.Label>
                    {savedMaterials.length === 0 ? (
                        <div className="text-muted p-3 border rounded" style={{ fontSize: 12, background: '#fafafa' }}>
                            No saved materials yet. Generate a lesson plan, lab session, or content in the other tabs, then save it to push here.
                        </div>
                    ) : (
                        <Form.Select value={selectedMaterial} onChange={e => { setSelectedMaterial(e.target.value); setPushed(false); }} style={{ fontSize: 13 }}>
                            <option value="">— Select material —</option>
                            {savedMaterials.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                        </Form.Select>
                    )}
                </Form.Group>

                {/* ── Moodle flow ── */}
                {platform === 'moodle' && (
                    <>
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                                Moodle Course
                                <button className="btn btn-link btn-sm p-0" onClick={loadMoodleCourses} disabled={loadingMoodleCourses} style={{ fontSize: 11 }}>
                                    {loadingMoodleCourses ? <Spinner size="sm" aria-hidden="true" /> : <RefreshCw size={12} />}
                                </button>
                            </Form.Label>
                            {moodleCourses.length === 0 && !loadingMoodleCourses ? (
                                <Alert variant="warning" className="mb-0" style={{ fontSize: 12 }}>No Moodle courses found. Configure Moodle in ExaminationOS Settings.</Alert>
                            ) : (
                                <Form.Select value={moodleCourseId} onChange={e => { setMoodleCourseId(e.target.value); setPushed(false); }} style={{ fontSize: 13 }} disabled={loadingMoodleCourses}>
                                    <option value="">— Select course —</option>
                                    {moodleCourses.map(c => <option key={c.id} value={c.id}>{c.fullname || c.shortname || c.id}</option>)}
                                </Form.Select>
                            )}
                        </Form.Group>
                        {pushed ? (
                            <Alert variant="success" style={{ fontSize: 12 }}>
                                ✓ Material posted to the Moodle course announcements forum.
                                <button className="btn btn-link btn-sm p-0 ms-2" onClick={() => setPushed(false)} style={{ fontSize: 11 }}>Push another</button>
                            </Alert>
                        ) : (
                            <Button onClick={handleMoodlePush} disabled={pushing || !selectedMaterial || !moodleCourseId} style={{ background: '#0dcaf0', border: 'none', color: '#fff', minWidth: 160 }}>
                                {pushing ? <><Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />Pushing…</> : <><Send size={15} className="me-2" />Push to Moodle</>}
                            </Button>
                        )}
                        <div className="text-muted mt-2" style={{ fontSize: 11 }}>Content is posted to the course's Announcements forum as a new discussion.</div>
                    </>
                )}

                {/* ── Google Classroom flow ── */}
                {platform === 'classroom' && (
                    <>
                        {(classroomStep === 'idle' || classroomStep === 'auth') && (
                            <div className="border rounded p-3 mb-3" style={{ background: '#f8f9fa' }}>
                                <div className="fw-bold mb-1" style={{ fontSize: 13 }}>Step 1 — Authorize Google</div>
                                <div className="text-muted mb-2" style={{ fontSize: 12 }}>Click below to sign in with Google and allow posting to your Classroom courses.</div>
                                <Button size="sm" variant="outline-primary" onClick={initClassroomAuth} disabled={classroomStep === 'auth'}>
                                    {classroomStep === 'auth' ? <><Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />Authorizing…</> : 'Authorize Google Classroom'}
                                </Button>
                            </div>
                        )}

                        {(classroomStep === 'courses' || classroomStep === 'pushing') && (
                            <>
                                <Alert variant="success" className="py-2 mb-3" style={{ fontSize: 12 }}>✓ Google Classroom connected</Alert>
                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold" style={{ fontSize: 12 }}>Classroom Course</Form.Label>
                                    {classroomCourses.length === 0 ? (
                                        <div className="text-muted" style={{ fontSize: 12 }}>No active courses found in your Google Classroom account.</div>
                                    ) : (
                                        <Form.Select value={classroomCourseId} onChange={e => setClassroomCourseId(e.target.value)} style={{ fontSize: 13 }}>
                                            <option value="">— Select course —</option>
                                            {classroomCourses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </Form.Select>
                                    )}
                                </Form.Group>
                                <Button onClick={handleClassroomPush} disabled={classroomStep === 'pushing' || !selectedMaterial || !classroomCourseId} style={{ background: '#0dcaf0', border: 'none', color: '#fff', minWidth: 180 }}>
                                    {classroomStep === 'pushing' ? <><Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />Pushing…</> : <><Send size={15} className="me-2" />Post to Classroom</>}
                                </Button>
                                <div className="text-muted mt-2" style={{ fontSize: 11 }}>Content is uploaded as a DOCX and posted under the course's Classwork → Materials tab.</div>
                            </>
                        )}

                        {classroomStep === 'done' && (
                            <Alert variant="success" style={{ fontSize: 12 }}>
                                ✓ Material uploaded as DOCX and posted to Classroom under Classwork → Materials.
                                <button className="btn btn-link btn-sm p-0 ms-2" onClick={resetClassroom} style={{ fontSize: 11 }}>Push another</button>
                            </Alert>
                        )}

                        {classroomStep === 'error' && (
                            <Alert variant="danger" style={{ fontSize: 12 }}>
                                {classroomError}
                                <button className="btn btn-link btn-sm p-0 ms-2" style={{ fontSize: 11 }} onClick={resetClassroom}>Try again</button>
                            </Alert>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Main TeachingCopilot Component ──────────────────────────────────────────
const TeachingCopilot = () => {
    const { slug: tenantSlug } = useParams();
    const dispatch = useDispatch();

    const [projects, setProjects] = useState([]);
    const [syllabi, setSyllabi] = useState({});
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [loadingSyllabusId, setLoadingSyllabusId] = useState(null);

    const [activeProject, setActiveProject] = useState(null);
    const [activeTopic, setActiveTopic] = useState(null);

    // Views: 'workspace' | 'analytics' | 'editor' | 'qbank'
    const [activeView, setActiveView] = useState('workspace');
    // Workspace tabs
    const [activeWorkspace, setActiveWorkspace] = useState('chat');

    const [editorContent, setEditorContent] = useState('');
    const [analyticsProject, setAnalyticsProject] = useState(null);
    const [analyticsTab, setAnalyticsTab] = useState('engagement');

    const [savedMaterials, setSavedMaterials] = useState([]);
    const [activeMaterialId, setActiveMaterialId] = useState(null);
    const [documentTitle, setDocumentTitle] = useState('Untitled Document');
    const [isSharedWithStudents, setIsSharedWithStudents] = useState(false);

    // Course-wide materials browser — the per-topic `savedMaterials` above
    // only ever shows documents saved against whichever topic is currently
    // selected, so a document saved under a different topic effectively
    // "disappears" from view. This lists every material for the course.
    const [materialsProject, setMaterialsProject] = useState(null);
    const [courseMaterials, setCourseMaterials] = useState([]);
    const [loadingCourseMaterials, setLoadingCourseMaterials] = useState(false);

    // Profile
    const [profile, setProfile] = useState(null);
    const [showProfile, setShowProfile] = useState(false);

    // Sidebar expand state (custom accordion)
    const [expandedProjectId, setExpandedProjectId] = useState(null);
    const [expandedUnitIdxMap, setExpandedUnitIdxMap] = useState({});

    // Q.Bank
    const [sidebarTab, setSidebarTab] = useState('courses');
    const [qbankReviews, setQbankReviews] = useState([]);
    const [qbankReviewsLoading, setQbankReviewsLoading] = useState(false);
    const [qbankBusy, setQbankBusy] = useState(null);
    const qbankPolled = useRef(false);

    const loadQbankReviews = useCallback(async () => {
        if (qbankReviewsLoading) return;
        setQbankReviewsLoading(true);
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('examToken');
            const res = await api.get(`/tenant/${tenantSlug}/exam/faculty/pending-approvals`, { headers: { Authorization: `Bearer ${token}` } });
            setQbankReviews(Array.isArray(res.data) ? res.data : (res.data.reviews || []));
        } catch { }
        finally { setQbankReviewsLoading(false); }
    }, [tenantSlug]); // eslint-disable-line

    useEffect(() => {
        if ((sidebarTab === 'qbank' || activeView === 'qbank') && !qbankPolled.current) {
            qbankPolled.current = true;
            loadQbankReviews();
        }
    }, [sidebarTab, activeView, loadQbankReviews]);

    // Load faculty profile on mount
    useEffect(() => {
        getCopilotProfile(tenantSlug).then(p => setProfile(p)).catch(() => {});
    }, [tenantSlug]);

    const handleProfileUpdated = (updated) => {
        setProfile(updated);
        dispatch(setUser({ ...updated, app_context: 'classroom', tenant_slug: tenantSlug }));
    };

    const handleQbankApprove = async (reviewId) => {
        setQbankBusy(reviewId);
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('examToken');
            await api.post(`/tenant/${tenantSlug}/exam/faculty/reviews/${reviewId}/approve`, null, { headers: { Authorization: `Bearer ${token}` } });
            showToast('Q.Bank approved.', 'success');
            qbankPolled.current = false;
            loadQbankReviews();
        } catch { showToast('Approval failed.', 'error'); }
        finally { setQbankBusy(null); }
    };

    const handleQbankReject = async (reviewId, notes) => {
        setQbankBusy(reviewId);
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('examToken');
            await api.post(`/tenant/${tenantSlug}/exam/faculty/reviews/${reviewId}/reject`, null, { params: notes ? { reviewer_notes: notes } : {}, headers: { Authorization: `Bearer ${token}` } });
            showToast('Q.Bank rejected.', 'success');
            qbankPolled.current = false;
            loadQbankReviews();
        } catch { showToast('Rejection failed.', 'error'); }
        finally { setQbankBusy(null); }
    };

    // PPTX modal
    const [showCreatePptxModal, setShowCreatePptxModal] = useState(false);
    const [pptxTitle, setPptxTitle] = useState('');
    const [pptxInstructions, setPptxInstructions] = useState('');
    const [pptxSlideCount, setPptxSlideCount] = useState(10);
    const [pptxTemplate, setPptxTemplate] = useState('academic_dark');
    const [isCreatingPptx, setIsCreatingPptx] = useState(false);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const res = await api.get(`/tenant/${tenantSlug}/my-faculty-projects`, { headers: { Authorization: `Bearer ${token}` } });
                setProjects(res.data);
                if (res.data.length > 0) handleProjectExpand(res.data[0].id.toString());
            } catch (err) { console.error('Failed to load projects', err); }
            finally { setLoadingProjects(false); }
        };
        fetchProjects();
    }, [tenantSlug]); // eslint-disable-line

    const handleProjectExpand = async (projectIdStr) => {
        const pId = parseInt(projectIdStr);
        if (syllabi[pId]) return;
        setLoadingSyllabusId(pId);
        try {
            const token = localStorage.getItem('access_token');
            const res = await api.get(`/tenant/${tenantSlug}/projects/${pId}/syllabus-tree`, { headers: { Authorization: `Bearer ${token}` } });
            setSyllabi(prev => ({ ...prev, [pId]: res.data }));
        } catch (err) { console.error(`Failed to load syllabus for project ${pId}`, err); }
        finally { setLoadingSyllabusId(null); }
    };

    const handleOpenInEditor = (markdownContent) => {
        setEditorContent(markdownContent);
        setActiveMaterialId(null);
        setDocumentTitle('New Generated Document');
        setIsSharedWithStudents(false);
        setActiveView('editor');
    };

    const handleCreatePptx = async () => {
        if (!activeTopic || !activeProject) return;
        setIsCreatingPptx(true);
        try {
            const token = localStorage.getItem('access_token');
            const resolvedTitle = pptxTitle.trim() || `${activeTopic.title} — Presentation`;
            const res = await api.post(
                `/tenant/${tenantSlug}/projects/${activeProject.id}/concepts/${activeTopic.id}/create-pptx`,
                { title: resolvedTitle, instructions: pptxInstructions, template_key: pptxTemplate, slide_count: pptxSlideCount },
                { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.setAttribute('download', `${resolvedTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pptx`);
            document.body.appendChild(a); a.click(); a.parentNode.removeChild(a);
            window.URL.revokeObjectURL(url);
            setShowCreatePptxModal(false); setPptxTitle(''); setPptxInstructions('');
            showToast('Presentation created and downloaded!', 'success');
        } catch { showToast('Failed to create presentation. Please try again.', 'error'); }
        finally { setIsCreatingPptx(false); }
    };

    const handleOpenExistingMaterial = async (materialId) => {
        try {
            const token = localStorage.getItem('access_token');
            const res = await api.get(`/tenant/${tenantSlug}/materials/${materialId}`, { headers: { Authorization: `Bearer ${token}` } });
            setEditorContent(res.data.content_body || '');
            setActiveMaterialId(materialId);
            setDocumentTitle(res.data.title || 'Untitled Document');
            setIsSharedWithStudents(!!res.data.is_shared_with_students);
            setActiveView('editor');
        } catch { showToast('Failed to load document.', 'error'); }
    };

    const loadCourseMaterials = async (project) => {
        setMaterialsProject(project);
        setActiveView('materials');
        setLoadingCourseMaterials(true);
        try {
            const token = localStorage.getItem('access_token');
            const res = await api.get(`/tenant/${tenantSlug}/projects/${project.id}/materials`, { headers: { Authorization: `Bearer ${token}` } });
            setCourseMaterials(res.data || []);
        } catch { setCourseMaterials([]); }
        finally { setLoadingCourseMaterials(false); }
    };

    const handleToggleShareCourseMaterial = async (materialId, nextValue) => {
        try {
            const token = localStorage.getItem('access_token');
            await api.patch(
                `/tenant/${tenantSlug}/materials/${materialId}/share`,
                { is_shared_with_students: nextValue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCourseMaterials(prev => prev.map(m => m.id === materialId ? { ...m, is_shared_with_students: nextValue } : m));
            showToast(nextValue ? 'Shared with students — visible in the AskOS content reader.' : 'Unshared. No longer visible to students.', 'success');
        } catch {
            showToast('Could not update sharing status.', 'error');
        }
    };

    const handleToggleShare = async (materialId, nextValue) => {
        try {
            const token = localStorage.getItem('access_token');
            await api.patch(
                `/tenant/${tenantSlug}/materials/${materialId}/share`,
                { is_shared_with_students: nextValue },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSavedMaterials(prev => prev.map(m => m.id === materialId ? { ...m, is_shared_with_students: nextValue } : m));
            if (activeMaterialId === materialId) setIsSharedWithStudents(nextValue);
            showToast(nextValue ? 'Shared with students — visible in the AskOS content reader.' : 'Unshared. No longer visible to students.', 'success');
        } catch {
            showToast('Could not update sharing status.', 'error');
        }
    };

    const handleSelectTopic = async (project, topic) => {
        setActiveProject(project);
        setActiveTopic(topic);
        setActiveView('workspace');
        setActiveWorkspace('chat');
        try {
            const token = localStorage.getItem('access_token');
            const res = await api.get(`/tenant/${tenantSlug}/projects/${project.id}/concepts/${topic.id}/materials`, { headers: { Authorization: `Bearer ${token}` } });
            setSavedMaterials(res.data);
        } catch { setSavedMaterials([]); }
    };

    const handleSaveContent = async (markdownContent) => {
        if (!activeTopic) return;
        try {
            const token = localStorage.getItem('access_token');
            const payload = { title: documentTitle, content_body: markdownContent, is_shared_with_students: isSharedWithStudents };
            if (activeMaterialId) {
                await api.put(`/tenant/${tenantSlug}/materials/${activeMaterialId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            } else {
                const res = await api.post(`/tenant/${tenantSlug}/projects/${activeProject.id}/concepts/${activeTopic.id}/materials`, payload, { headers: { Authorization: `Bearer ${token}` } });
                setActiveMaterialId(res.data.id);
                setSavedMaterials(prev => [...prev, { id: res.data.id, title: documentTitle }]);
            }
        } catch { console.error('Failed to save content'); }
    };

    // Workspace tabs config
    const workspaceTabs = [
        { key: 'chat', label: 'Chat', icon: <Zap size={14} />, color: '#0d6efd' },
        { key: 'content', label: 'Content Studio', icon: <Upload size={14} />, color: '#6f42c1' },
        { key: 'lesson', label: 'Lesson Planner', icon: <Calendar size={14} />, color: '#198754' },
        { key: 'lab', label: 'Lab Designer', icon: <FlaskConical size={14} />, color: '#fd7e14' },
        { key: 'quiz', label: 'Quiz Studio', icon: <Trophy size={14} />, color: '#dc3545' },
        { key: 'push', label: 'Push to LMS', icon: <Send size={14} />, color: '#0dcaf0' },
    ];

    const { user } = useSelector(s => s.user || {});
    const displayName = profile?.display_name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || user?.email?.split('@')[0] || 'Faculty';
    const photoUrl = profile?.profile_photo || null;
    const pendingQbank = qbankReviews.filter(r => r.status === 'pending').length;

    const handleQbankNavClick = () => {
        setSidebarTab('qbank');
        setActiveView('qbank');
        if (!qbankPolled.current) { qbankPolled.current = true; loadQbankReviews(); }
    };

    // Sidebar nav items
    const NAV = [
        { key: 'courses', icon: <BookOpen size={16} />, label: 'Courses', view: null },
        { key: 'analytics', icon: <BarChart2 size={16} />, label: 'Analytics', view: null },
        { key: 'qbank', icon: <BookOpenCheck size={16} />, label: 'Q.Bank Reviews', badge: pendingQbank, view: 'qbank' },
    ];

    const SIDEBAR_BG = 'linear-gradient(180deg,#1a1a2e 0%,#16213e 60%,#0f3460 100%)';

    // Custom sidebar expand helpers
    const toggleProject = (projectId) => {
        const isOpen = expandedProjectId === projectId;
        setExpandedProjectId(isOpen ? null : projectId);
        if (!isOpen && !syllabi[projectId]) handleProjectExpand(String(projectId));
    };
    const toggleUnit = (projectId, idx) => {
        setExpandedUnitIdxMap(m => ({ ...m, [`${projectId}_${idx}`]: !m[`${projectId}_${idx}`] }));
    };

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Segoe UI',Arial,sans-serif" }}>

        {/* ── Main row (sidebar + workspace + right panel) ─────────────── */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

            {/* ── LEFT SIDEBAR (dark navy) ─────────────────────────────────── */}
            <div style={{ width: 260, flexShrink: 0, background: SIDEBAR_BG, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Brand */}
                <div style={{ padding: '20px 20px 14px', borderBottom: '1px solid rgba(255,255,255,.08)', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Sparkles size={16} color="#fff" />
                        </div>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Teaching Copilot</div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>AcademicOS Faculty</div>
                        </div>
                    </div>

                    {/* Profile card */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                        <div style={{ position: 'relative' }}>
                            <FacultyAvatar name={displayName} size={62} photoUrl={photoUrl} onClick={() => setShowProfile(true)} />
                            <div onClick={() => setShowProfile(true)} style={{ position: 'absolute', bottom: 0, right: 0, width: 18, height: 18, borderRadius: '50%', background: '#6366f1', border: '2px solid #1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 9 }}>✏️</div>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginTop: 8 }}>{displayName}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.55)', textTransform: 'capitalize' }}>{(profile?.role || user?.role || 'Faculty').replace(/_/g,' ')}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', marginTop: 2 }}>{profile?.email || user?.email}</div>
                    </div>

                    {/* Clock */}
                    <div style={{ marginTop: 16 }}>
                        <LiveClock />
                    </div>
                </div>

                {/* Nav */}
                <div style={{ padding: '12px 12px 0', flexShrink: 0 }}>
                    {NAV.map(n => (
                        <button key={n.key} onClick={() => {
                            setSidebarTab(n.key);
                            if (n.key === 'qbank') { handleQbankNavClick(); }
                            else if (activeView === 'qbank') { setActiveView('workspace'); }
                        }}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                                borderRadius: 10, border: 'none', cursor: 'pointer', marginBottom: 4,
                                background: sidebarTab === n.key ? 'rgba(99,102,241,.25)' : 'transparent',
                                color: sidebarTab === n.key ? '#a5b4fc' : 'rgba(255,255,255,.55)',
                                fontWeight: sidebarTab === n.key ? 700 : 400, fontSize: 13,
                                transition: 'all .15s',
                            }}>
                            {n.icon}
                            <span style={{ flex: 1, textAlign: 'left' }}>{n.label}</span>
                            {n.badge > 0 && (
                                <span style={{ background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 10, padding: '1px 6px' }}>{n.badge}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Course / Q.Bank list */}
                <div className="overflow-auto flex-grow-1" style={{ padding: '8px 12px 12px' }}>

                    {/* ── Courses (custom expand/collapse) ── */}
                    {sidebarTab === 'courses' && (
                        <>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: 1, padding: '10px 4px 6px' }}>Your Courses</div>
                            {loadingProjects
                                ? <div className="text-center py-4"><Spinner size="sm" style={{ color: 'rgba(255,255,255,.4)' }} aria-hidden="true" /></div>
                                : projects.map(project => {
                                    const isOpen = expandedProjectId === project.id;
                                    return (
                                        <div key={project.id} style={{ marginBottom: 3 }}>
                                            {/* Project row */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderRadius: 8, background: isOpen ? 'rgba(99,102,241,.2)' : 'rgba(255,255,255,.07)', cursor: 'pointer' }}
                                                onClick={() => toggleProject(project.id)}>
                                                <BookOpen size={13} color="#a5b4fc" style={{ flexShrink: 0 }} />
                                                <span style={{ fontSize: 12, fontWeight: 600, color: '#f1f5f9', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.name}</span>
                                                <button title="All saved materials for this course" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#67e8f9', flexShrink: 0 }}
                                                    onClick={e => { e.stopPropagation(); loadCourseMaterials(project); }}>
                                                    <FolderOpen size={12} />
                                                </button>
                                                <button style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#86efac', flexShrink: 0 }}
                                                    onClick={e => { e.stopPropagation(); setAnalyticsProject(project); setActiveView('analytics'); setSidebarTab('analytics'); }}>
                                                    <BarChart2 size={12} />
                                                </button>
                                                <span style={{ color: 'rgba(255,255,255,.45)', fontSize: 10, flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
                                            </div>
                                            {/* Units + Topics */}
                                            {isOpen && (
                                                <div style={{ paddingLeft: 8, paddingTop: 2 }}>
                                                    {loadingSyllabusId === project.id
                                                        ? <div className="text-center py-2"><Spinner size="sm" style={{ color: '#a5b4fc' }} aria-hidden="true" /></div>
                                                        : (syllabi[project.id] || []).map((unit, idx) => {
                                                            const unitKey = `${project.id}_${idx}`;
                                                            const unitOpen = !!expandedUnitIdxMap[unitKey];
                                                            const hasTopics = (unit.topics || []).length > 0;
                                                            // Concepts with no child topics still map 1:1 to a real
                                                            // concept_id — the whole content/activity pipeline keys
                                                            // off concept_id, not topic_id, so let the concept row
                                                            // itself be selected directly instead of only expanding.
                                                            const unitSelected = !hasTopics && activeTopic?.id === unit.id;
                                                            return (
                                                                <div key={unit.id || idx} style={{ marginBottom: 2 }}>
                                                                    <div style={{
                                                                            display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                                                                            background: unitSelected ? 'rgba(99,102,241,.4)' : 'transparent',
                                                                            color: unitSelected ? '#a5b4fc' : '#c7d2fe',
                                                                        }}
                                                                        title={hasTopics ? undefined : 'No sub-topics — click to work on this concept directly'}
                                                                        onClick={() => hasTopics ? toggleUnit(project.id, idx) : handleSelectTopic(project, unit)}>
                                                                        <span style={{ fontSize: 10, flexShrink: 0 }}>{hasTopics ? (unitOpen ? '▾' : '▸') : '●'}</span>
                                                                        <span style={{ fontSize: 11, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{unit.title}</span>
                                                                    </div>
                                                                    {unitOpen && (
                                                                        <div style={{ paddingLeft: 12 }}>
                                                                            {(unit.topics || []).map(topic => (
                                                                                <button key={topic.id} onClick={() => handleSelectTopic(project, topic)}
                                                                                    style={{
                                                                                        width: '100%', display: 'flex', alignItems: 'center', gap: 6,
                                                                                        padding: '5px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                                                                        marginBottom: 1, textAlign: 'left',
                                                                                        background: activeTopic?.id === topic.id ? 'rgba(99,102,241,.4)' : 'transparent',
                                                                                        color: activeTopic?.id === topic.id ? '#a5b4fc' : 'rgba(255,255,255,.75)',
                                                                                        fontSize: 11,
                                                                                    }}>
                                                                                    <FileText size={10} style={{ flexShrink: 0, opacity: .7 }} />
                                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topic.title}</span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            }
                        </>
                    )}

                    {/* ── Analytics quick-select ── */}
                    {sidebarTab === 'analytics' && (
                        <>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: 1, padding: '10px 4px 6px' }}>Select Course</div>
                            {projects.map(p => (
                                <button key={p.id} onClick={() => { setAnalyticsProject(p); setActiveView('analytics'); setSidebarTab('analytics'); }}
                                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', marginBottom: 4, textAlign: 'left', background: analyticsProject?.id === p.id ? 'rgba(99,102,241,.25)' : 'rgba(255,255,255,.07)', color: '#e2e8f0', fontSize: 12 }}>
                                    <BarChart2 size={13} color="#86efac" />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                                </button>
                            ))}
                        </>
                    )}

                    {/* ── Q.Bank sidebar summary (full list is in main workspace) ── */}
                    {sidebarTab === 'qbank' && (
                        <div style={{ paddingTop: 10 }}>
                            <div style={{ background: 'rgba(99,102,241,.15)', border: '1px solid rgba(99,102,241,.3)', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                                <div style={{ fontSize: 28, fontWeight: 800, color: pendingQbank > 0 ? '#fbbf24' : '#86efac' }}>{pendingQbank}</div>
                                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>pending review{pendingQbank !== 1 ? 's' : ''}</div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', marginTop: 6 }}>Full list shown in the workspace →</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── CENTRE + RIGHT ───────────────────────────────────────────── */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* MAIN WORKSPACE */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8faff' }}>

                    {/* Analytics overlay */}
                    {activeView === 'analytics' && analyticsProject && (
                        <div style={{ height: '100%', background: '#fff' }}>
                            <StudentAnalyticsPanel
                                tenantSlug={tenantSlug} project={analyticsProject}
                                activeTab={analyticsTab} setActiveTab={setAnalyticsTab}
                                onBack={() => { setActiveView('workspace'); setSidebarTab('courses'); }}
                            />
                        </div>
                    )}

                    {/* Course-wide saved materials browser */}
                    {activeView === 'materials' && materialsProject && (
                        <div style={{ height: '100%', background: '#fff', overflow: 'auto', padding: '20px 24px' }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <Button variant="light" size="sm" onClick={() => setActiveView('workspace')}><ArrowLeft size={14} /></Button>
                                <h5 className="mb-0 fw-bold">Saved Materials — {materialsProject.name}</h5>
                            </div>
                            <p className="text-muted small mb-3">Every document saved from the Teaching Editor across this course, regardless of which topic it was saved against. Toggle the switch to share a document with students in the AskOS content reader.</p>
                            {loadingCourseMaterials ? (
                                <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                            ) : courseMaterials.length === 0 ? (
                                <Alert variant="secondary" className="small">No saved materials yet for this course. Save a document from any topic's Content Studio / Chat workspace and it will show up here.</Alert>
                            ) : (
                                <Table hover size="sm">
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th>Topic / Concept</th>
                                            <th>Type</th>
                                            <th>Last updated</th>
                                            <th>Shared with students</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {courseMaterials.map(m => (
                                            <tr key={m.id}>
                                                <td className="small">{m.title}</td>
                                                <td className="small text-muted">{m.concept_name || '—'}</td>
                                                <td className="small text-muted">{m.material_type}</td>
                                                <td className="small text-muted">{m.updated_at ? new Date(m.updated_at).toLocaleString() : '—'}</td>
                                                <td>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`course-share-switch-${m.id}`}
                                                        checked={!!m.is_shared_with_students}
                                                        onChange={e => handleToggleShareCourseMaterial(m.id, e.target.checked)}
                                                        title={m.is_shared_with_students ? 'Shared with students — click to unshare' : 'Not shared — click to share with students'}
                                                    />
                                                </td>
                                                <td className="text-end">
                                                    <Button variant="outline-secondary" size="sm" onClick={() => handleOpenExistingMaterial(m.id)}>Open</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    )}

                    {/* Q.Bank workspace overlay */}
                    {activeView === 'qbank' && (
                        <div style={{ height: '100%' }}>
                            <QBankWorkspace
                                tenantSlug={tenantSlug}
                                reviews={qbankReviews}
                                loading={qbankReviewsLoading}
                                onApprove={handleQbankApprove}
                                onReject={handleQbankReject}
                                onRefresh={() => { qbankPolled.current = false; loadQbankReviews(); }}
                            />
                        </div>
                    )}

                        {/* Editor overlay */}
                        {activeView === 'editor' && (
                            <div className="h-100 d-flex flex-column">
                                <div className="p-2 border-bottom bg-white d-flex align-items-center justify-content-between flex-shrink-0">
                                    <Button variant="link" className="text-decoration-none text-dark d-flex align-items-center gap-1" onClick={() => setActiveView('workspace')}>
                                        <ArrowLeft size={16} /> Back to Workspace
                                    </Button>
                                    <div className="d-flex align-items-center gap-3 pe-3">
                                        <Form.Check
                                            type="switch"
                                            id="share-with-students-switch"
                                            label={<small className="fw-semibold">Share with students</small>}
                                            checked={isSharedWithStudents}
                                            onChange={e => setIsSharedWithStudents(e.target.checked)}
                                            disabled={!activeMaterialId}
                                            title={!activeMaterialId ? 'Save the document first, then share it' : 'Visible in the student content reader once saved'}
                                        />
                                        <small className="text-muted fw-bold">Editing: {activeTopic?.title}</small>
                                    </div>
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                    <TeachingMaterialEditor initialContent={editorContent} tenantSlug={tenantSlug} projectId={activeProject?.id} onSave={handleSaveContent} documentTitle={documentTitle} setDocumentTitle={setDocumentTitle} />
                                </div>
                            </div>
                        )}

                    {/* Workspace view */}
                    {activeView === 'workspace' && (
                        <>
                            {!activeTopic ? (
                                <CopilotDashboard
                                    projects={projects}
                                    qbankReviews={qbankReviews}
                                    displayName={displayName}
                                    onViewAnalytics={p => { setAnalyticsProject(p); setActiveView('analytics'); setSidebarTab('analytics'); }}
                                />
                            ) : (
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                    {/* Topic header */}
                                    <div style={{ background: '#fff', borderBottom: '1px solid #e9ecef', flexShrink: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', gap: 10, borderBottom: '1px solid #f1f3f5' }}>
                                            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <Sparkles size={14} color="#fff" />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeTopic.title}</div>
                                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{activeProject.name}</div>
                                            </div>
                                            {savedMaterials.length > 0 && (
                                                <Dropdown align="end">
                                                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1" style={{ fontSize: 11 }}>
                                                        <FolderOpen size={13} /> Saved ({savedMaterials.length})
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu style={{ minWidth: 260 }}>
                                                        {savedMaterials.map(mat => (
                                                            <Dropdown.Item
                                                                key={mat.id}
                                                                onClick={() => handleOpenExistingMaterial(mat.id)}
                                                                style={{ fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
                                                            >
                                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0 }}>
                                                                    <FileText size={13} className="text-muted flex-shrink-0" />
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mat.title}</span>
                                                                </span>
                                                                <Form.Check
                                                                    type="switch"
                                                                    id={`share-switch-${mat.id}`}
                                                                    className="flex-shrink-0"
                                                                    checked={!!mat.is_shared_with_students}
                                                                    onClick={e => e.stopPropagation()}
                                                                    onChange={e => handleToggleShare(mat.id, e.target.checked)}
                                                                    title={mat.is_shared_with_students ? 'Shared with students — click to unshare' : 'Not shared — click to share with students'}
                                                                />
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            )}
                                            <Button variant="outline-success" size="sm" className="d-flex align-items-center gap-1" style={{ fontSize: 11 }} onClick={() => setShowCreatePptxModal(true)}>
                                                <Presentation size={13} /> PPTX
                                            </Button>
                                        </div>
                                        {/* Tab bar */}
                                        <div style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none' }}>
                                            {workspaceTabs.map(tab => (
                                                <button key={tab.key} onClick={() => setActiveWorkspace(tab.key)} style={{
                                                    padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0,
                                                    display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                                                    color: activeWorkspace === tab.key ? tab.color : '#6c757d',
                                                    fontWeight: activeWorkspace === tab.key ? 700 : 400,
                                                    borderBottom: activeWorkspace === tab.key ? `2px solid ${tab.color}` : '2px solid transparent',
                                                    transition: 'all .15s',
                                                }}>{tab.icon} {tab.label}</button>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Workspace content */}
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        {activeWorkspace === 'chat' && <TeachingCopilotChat key={`chat-${activeTopic.id}`} projectId={activeProject.id} tenantSlug={tenantSlug} conceptId={activeTopic.id} onOpenInEditor={handleOpenInEditor} onGenerateSlides={() => setShowCreatePptxModal(true)} />}
                                        {activeWorkspace === 'content' && <ContentStudioWorkspace tenantSlug={tenantSlug} project={activeProject} topic={activeTopic} onOpenInEditor={handleOpenInEditor} />}
                                        {activeWorkspace === 'lesson' && <LessonPlannerWorkspace tenantSlug={tenantSlug} project={activeProject} topic={activeTopic} onOpenInEditor={handleOpenInEditor} />}
                                        {activeWorkspace === 'lab' && <LabDesignerWorkspace tenantSlug={tenantSlug} project={activeProject} topic={activeTopic} onOpenInEditor={handleOpenInEditor} />}
                                        {activeWorkspace === 'quiz' && <QuizStudioWorkspace tenantSlug={tenantSlug} project={activeProject} topic={activeTopic} />}
                                        {activeWorkspace === 'push' && <PushToLMSWorkspace tenantSlug={tenantSlug} project={activeProject} topic={activeTopic} savedMaterials={savedMaterials} />}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>{/* end MAIN WORKSPACE */}

                {/* ── RIGHT PANEL ──────────────────────────────────────────── */}
                {activeView !== 'editor' && (
                    <div style={{ width: 268, flexShrink: 0, background: '#f8faff', borderLeft: '1px solid #e9ecef', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px 14px', gap: 16, overflowY: 'auto' }}>

                        {/* Calendar */}
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Calendar</div>
                            <MiniCalendar />
                        </div>

                        {/* Q.Bank Reviews summary */}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Q.Bank Reviews</div>
                                {pendingQbank > 0 && (
                                    <span style={{ background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 10, padding: '2px 7px' }}>{pendingQbank} pending</span>
                                )}
                            </div>
                            {qbankReviewsLoading ? (
                                <div className="text-center py-2"><Spinner size="sm" aria-hidden="true" /></div>
                            ) : qbankReviews.length === 0 ? (
                                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', fontSize: 11, color: '#166534' }}>
                                    ✓ All Q.Bank reviews are up to date.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {qbankReviews.slice(0, 3).map(r => (
                                        <div key={r.review_id} style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 10, padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.set_name || r.topic_name || 'Q.Bank Set'}</div>
                                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                <span style={{ fontSize: 9, background: r.status === 'pending' ? '#fef9c3' : '#f0fdf4', color: r.status === 'pending' ? '#854d0e' : '#166534', border: `1px solid ${r.status === 'pending' ? '#fde68a' : '#bbf7d0'}`, borderRadius: 20, padding: '1px 7px', fontWeight: 600 }}>
                                                    {r.status}
                                                </span>
                                                <button className="btn btn-sm p-0 border-0 ms-auto" style={{ fontSize: 10, color: '#6366f1' }}
                                                    onClick={handleQbankNavClick}>Review →</button>
                                            </div>
                                        </div>
                                    ))}
                                    {qbankReviews.length > 3 && (
                                        <button style={{ background: 'none', border: 'none', fontSize: 11, color: '#6366f1', cursor: 'pointer', textAlign: 'center' }}
                                            onClick={handleQbankNavClick}>+ {qbankReviews.length - 3} more reviews</button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Quick stats */}
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Quick Stats</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[
                                    { label: 'Assigned Courses', value: projects.length, icon: '📚', color: '#6366f1' },
                                    { label: 'Saved Materials', value: savedMaterials.length, icon: '📄', color: '#0891b2' },
                                    { label: 'Pending Reviews', value: pendingQbank, icon: '📝', color: pendingQbank > 0 ? '#dc2626' : '#10b981' },
                                ].map((s, i) => (
                                    <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
                                        <span style={{ fontSize: 18 }}>{s.icon}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.label}</div>
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                )}{/* end RIGHT PANEL */}

            </div>{/* end CENTRE + RIGHT */}

        </div>{/* end main row */}

        <CopilotFooter />

            {/* Profile Settings Modal */}
            <ProfileSettingsModal
                show={showProfile}
                onHide={() => setShowProfile(false)}
                tenantSlug={tenantSlug}
                profile={profile}
                onProfileUpdated={handleProfileUpdated}
            />

            {/* Create PPTX Modal */}
            <Modal show={showCreatePptxModal} onHide={() => !isCreatingPptx && setShowCreatePptxModal(false)} centered size="xl">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center gap-2 fw-bold">
                        <Presentation className="text-success" size={22} /> Create AI Presentation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                        AI will generate a fully structured PPTX for <strong>{activeTopic?.title}</strong> and download it instantly.
                    </p>
                    <div className="d-flex gap-3 mb-3">
                        <Form.Group className="flex-grow-1">
                            <Form.Label className="fw-bold" style={{ fontSize: 13 }}>Presentation Title <span className="text-muted fw-normal">(optional)</span></Form.Label>
                            <Form.Control type="text" placeholder={`${activeTopic?.title || ''} — Presentation`} value={pptxTitle} onChange={e => setPptxTitle(e.target.value)} disabled={isCreatingPptx} style={{ fontSize: 13 }} />
                        </Form.Group>
                        <Form.Group style={{ width: 150 }}>
                            <Form.Label className="fw-bold" style={{ fontSize: 13 }}>Number of Slides</Form.Label>
                            <Form.Select value={pptxSlideCount} onChange={e => setPptxSlideCount(Number(e.target.value))} disabled={isCreatingPptx} style={{ fontSize: 13 }}>
                                {[6, 8, 10, 12, 15, 20].map(n => <option key={n} value={n}>{n} slides</option>)}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold" style={{ fontSize: 13 }}>Instructions <span className="text-muted fw-normal">(optional)</span></Form.Label>
                        <Form.Control as="textarea" rows={2} placeholder="e.g. Focus on clinical applications, include treatment comparisons, add a key statistics slide..." value={pptxInstructions} onChange={e => setPptxInstructions(e.target.value)} disabled={isCreatingPptx} style={{ fontSize: 13 }} />
                    </Form.Group>
                    <Form.Label className="fw-bold d-block mb-2" style={{ fontSize: 13 }}>Select Theme</Form.Label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                        {[
                            { key: 'academic_dark', label: 'Academic Dark', bg: '#1A1F2E', titleC: '#FFFFFF', bodyC: '#B0BEC5', accent: '#4FC3F7' },
                            { key: 'clean_light', label: 'Clean Light', bg: '#FFFFFF', titleC: '#1F4E79', bodyC: '#2D2D2D', accent: '#F96167' },
                            { key: 'institution_brand', label: 'Institution Brand', bg: '#F5F7FA', titleC: '#1A3C5E', bodyC: '#333333', accent: '#F5A623' },
                            { key: 'midnight_blue', label: 'Midnight Blue', bg: '#0D1B2A', titleC: '#E8F4FD', bodyC: '#A8D8EA', accent: '#00B4D8' },
                            { key: 'forest_green', label: 'Forest Green', bg: '#1B3A2D', titleC: '#F0FFF0', bodyC: '#B7DDB7', accent: '#76C442' },
                            { key: 'sunrise', label: 'Sunrise', bg: '#FFF8F0', titleC: '#7B2D00', bodyC: '#4A3728', accent: '#FF6B35' },
                            { key: 'minimal_mono', label: 'Minimal', bg: '#FAFAFA', titleC: '#111111', bodyC: '#444444', accent: '#888888' },
                            { key: 'royal_purple', label: 'Royal Purple', bg: '#2D1B69', titleC: '#FFFFFF', bodyC: '#C9B8FF', accent: '#A78BFA' },
                            { key: 'ocean_teal', label: 'Ocean Teal', bg: '#0A3D62', titleC: '#FFFFFF', bodyC: '#A8D8EA', accent: '#38D9A9' },
                            { key: 'charcoal_red', label: 'Charcoal Red', bg: '#2C2C2C', titleC: '#FFFFFF', bodyC: '#CCCCCC', accent: '#E53935' },
                            { key: 'rose_gold', label: 'Rose Gold', bg: '#FDF6F0', titleC: '#8B2252', bodyC: '#5C3D4E', accent: '#D4A5A5' },
                            { key: 'slate_corporate', label: 'Corporate Slate', bg: '#2F3E50', titleC: '#FFFFFF', bodyC: '#B0BEC5', accent: '#1ABC9C' },
                        ].map(theme => (
                            <div key={theme.key} onClick={() => !isCreatingPptx && setPptxTemplate(theme.key)}
                                style={{ background: theme.bg, border: pptxTemplate === theme.key ? '3px solid #0d6efd' : '2px solid #dee2e6', borderRadius: 8, padding: '10px 10px 8px', cursor: isCreatingPptx ? 'default' : 'pointer', transition: 'border 0.15s', userSelect: 'none', boxShadow: pptxTemplate === theme.key ? '0 0 0 3px rgba(13,110,253,0.25)' : 'none' }}>
                                <div style={{ background: theme.accent, height: 3, borderRadius: 2, marginBottom: 7 }} />
                                <div style={{ color: theme.titleC, fontWeight: 700, fontSize: 9, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeTopic?.title || 'Presentation Title'}</div>
                                <div style={{ color: theme.bodyC, fontSize: 8, lineHeight: 1.6 }}><div>&#8226; Key concept one</div><div>&#8226; Key concept two</div><div>&#8226; Key concept three</div></div>
                                <div style={{ color: theme.accent, fontSize: 8, marginTop: 6, fontWeight: 600, textAlign: 'center', borderTop: `1px solid ${theme.accent}33`, paddingTop: 5 }}>{theme.label}</div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreatePptxModal(false)} disabled={isCreatingPptx}>Cancel</Button>
                    <Button variant="success" onClick={handleCreatePptx} disabled={isCreatingPptx || !activeTopic}>
                        {isCreatingPptx ? <><Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />Generating &amp; Downloading&hellip;</> : <><Presentation size={15} className="me-2" />Generate &amp; Download PPTX</>}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TeachingCopilot;
